import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import { Hero, SegControl, EmptyState, ToggleSwitch, Dropdown } from "../components/adminSurface/AdminSurface";
import { UsersArt } from "../components/adminSurface/heroArt";
import ConfirmModal from "../components/common/ConfirmModal";
import BrandingTab from "../components/delivery/BrandingTab";
import {
  getDatabases,
  getDeliveryPolicy, saveDeliveryPolicy,
  getDeliveryFieldMaps, createDeliveryFieldMap, deleteDeliveryFieldMap,
  getDeliveryRecipients, createDeliveryRecipient, updateDeliveryRecipient,
  issueDeliveryAccessCode, clearDeliveryAccessCode, unlockDeliveryRecipient,
  getDeliveryRecipientKeys, addDeliveryRecipientKey, deleteDeliveryRecipientKey,
  getDeliveryIssuers, createDeliveryIssuer, deleteDeliveryIssuer,
  getDeliveryGrants, createDeliveryGrant, revokeDeliveryGrant,
  getDeliveryBatchReadiness, generateDeliveryBatchLinks, parseBatchNdjson,
  getDeliveryBatchRuns, getDeliveryBatchRevocations, revokeDeliveryBatchLinks,
  createColumnIndex,
  getDeliveryVerification, saveDeliveryVerification, previewDeliveryVerification,
} from "../api/adminApi";

/**
 * DELIVERY / SELF-SERVICE PORTAL administration.
 *
 * Four tabs that follow the order an operator actually sets this up in: map the recipient-key column on each
 * document base (Sources), create the people (Recipients), hand out revocable links (Deliveries), and set the
 * bounds everything is minted inside (Settings).
 *
 * Two screens here show a secret exactly once — an access code and a delivery link. Both are held in local
 * state only until dismissed, because the server kept nothing but a hash and no amount of clicking will bring
 * them back. The UI says so plainly rather than letting an operator discover it later.
 */

function dbId(d) { return d?.id ?? d?.Id ?? d?.dbInfoId ?? d?.DBInfoId; }
function dbNameOf(d) { return d?.dbName ?? d?.DbName ?? d?.name ?? d?.Name ?? String(dbId(d)); }

function fmtAbs(raw) {
  if (!raw) return "—";
  try {
    const d = new Date(raw);
    if (isNaN(d)) return String(raw);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
  } catch { return String(raw); }
}

/** Days is how operators think about link validity; the API speaks seconds. */
const DAY = 86400;

export default function DeliveryPage() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [tab, setTab] = useState("recipients");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const [policy, setPolicy] = useState(null);
  const [databases, setDatabases] = useState([]);
  const [maps, setMaps] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [grants, setGrants] = useState([]);
  const [issuers, setIssuers] = useState([]);
  const [algorithms, setAlgorithms] = useState([]);
  const [batchRuns, setBatchRuns] = useState([]);
  const [batchRevocations, setBatchRevocations] = useState(null);

  // A secret the server will never show again: { kind: "code" | "link", value, extra }
  const [oneTime, setOneTime] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const org = policy?.organization || "";

  const fail = useCallback((e) => {
    setError(e?.message || String(e));
    setNotice("");
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const p = await getDeliveryPolicy(token);
      setPolicy(p);
      const organization = p?.organization;
      const [dbs, m, r, g, i] = await Promise.all([
        getDatabases(token).catch(() => []),
        getDeliveryFieldMaps(token).catch(() => ({ fieldMaps: [] })),
        getDeliveryRecipients(token, { organization, includeDisabled: true }).catch(() => ({ recipients: [] })),
        getDeliveryGrants(token, { organization }).catch(() => ({ grants: [] })),
        getDeliveryIssuers(token, organization).catch(() => ({ issuers: [] })),
      ]);
      setDatabases(Array.isArray(dbs) ? dbs : dbs?.databases || []);
      setMaps(m?.fieldMaps || []);
      setRecipients(r?.recipients || []);
      setGrants(g?.grants || []);
      setIssuers(i?.issuers || []);
      setAlgorithms(i?.algorithms || ["RS256"]);

      // Batch state loads best-effort and separately: it is gated on its own switch, and a tenant that
      // never turns batch links on should not see the rest of the page fail because of them.
      const [runs, revs] = await Promise.all([
        getDeliveryBatchRuns(token, organization, 50).catch(() => ({ runs: [] })),
        getDeliveryBatchRevocations(token, organization).catch(() => null),
      ]);
      setBatchRuns(runs?.runs || []);
      setBatchRevocations(revs);
    } catch (e) {
      fail(e);
    } finally {
      setLoading(false);
    }
  }, [token, fail]);

  useEffect(() => { load(); }, [load]);

  const run = useCallback(async (fn, successMessage) => {
    setBusy(true);
    setError("");
    try {
      const result = await fn();
      if (successMessage) setNotice(successMessage);
      return result;
    } catch (e) {
      fail(e);
      return null;
    } finally {
      setBusy(false);
    }
  }, [fail]);

  const mappedDbIds = useMemo(() => new Set(maps.filter((m) => m.isActive).map((m) => m.dbInfoId)), [maps]);

  // ── Policy ─────────────────────────────────────────────────────────────────────────
  const togglePortal = async (next) => {
    const saved = await run(
      () => saveDeliveryPolicy(token, { organization: org, enabled: next }),
      next ? t("delivery.portalOn", { defaultValue: "The portal is now open for this organization." })
           : t("delivery.portalOff", { defaultValue: "The portal is closed for this organization." }));
    if (saved) setPolicy(saved);
  };

  const savePolicyFields = async (patch) => {
    const saved = await run(() => saveDeliveryPolicy(token, { organization: org, ...patch }),
      t("delivery.settingsSaved", { defaultValue: "Delivery settings saved." }));
    if (saved) setPolicy(saved);
    return saved;
  };

  // ── Batch links ────────────────────────────────────────────────────────────────────
  const loadBatch = useCallback(async () => {
    if (!token) return;
    const [runs, revs] = await Promise.all([
      getDeliveryBatchRuns(token, org, 50).catch(() => ({ runs: [] })),
      getDeliveryBatchRevocations(token, org).catch(() => null),
    ]);
    setBatchRuns(runs?.runs || []);
    setBatchRevocations(revs);
  }, [token, org]);

  const revokeBatchKey = (key) => {
    setConfirm({
      title: t("delivery.bulkBlockTitle", { defaultValue: "Block this customer's links?" }),
      body: t("delivery.bulkBlockBody", {
        key,
        defaultValue:
          "Every link you have sent to {{key}} stops working straight away, including a page they already have " +
          "open. No other customer is affected, and you can send them new links afterwards.",
      }),
      variant: "warn",
      onConfirm: async () => {
        setConfirm(null);
        const r = await run(
          () => revokeDeliveryBatchLinks(token, { organization: org, recipientKey: key, reason: "Revoked from the admin console" }),
          t("delivery.bulkBlockedDone", { defaultValue: "That customer's links have been blocked." }));
        if (r) await loadBatch();
      },
    });
  };

  // Deliberately a two-step confirmation with the count spelled out. This is the one action on the page
  // that can drop every customer of the tenant at once, and the API refuses it without an explicit flag
  // for the same reason — the dialog must not be the only thing standing in front of it, but it must
  // also not be a dialog somebody can dismiss out of habit.
  const revokeBatchAll = () => {
    setConfirm({
      title: t("delivery.bulkStopAllTitle", { defaultValue: "Stop every link for every customer?" }),
      body: t("delivery.bulkStopAllBody", {
        defaultValue:
          "Every link this organization has ever sent out stops working — potentially every customer who was " +
          "sent a statement. Anyone with a page open loses it on their next click. There is no undo: you would " +
          "have to create a new run and send the links out again. Customers you blocked individually stay blocked.",
      }),
      variant: "danger",
      icon: "⛔",
      onConfirm: async () => {
        setConfirm(null);
        const r = await run(
          () => revokeDeliveryBatchLinks(token, {
            organization: org, confirmOrganizationWide: true, reason: "Organization-wide revocation from the admin console",
          }),
          t("delivery.bulkStoppedAllDone", { defaultValue: "Every link for this organization has been stopped." }));
        if (r) { await loadBatch(); await load(); }
      },
    });
  };

  // ── Actions ────────────────────────────────────────────────────────────────────────
  const addMap = async (form) => {
    const created = await run(() => createDeliveryFieldMap(token, form),
      t("delivery.mapAdded", { defaultValue: "Recipient key column mapped." }));
    if (created) setMaps((prev) => [...prev.filter((m) => m.id !== created.id), created]);
  };

  const removeMap = (row) => setConfirm({
    title: t("delivery.removeMapTitle", { defaultValue: "Stop delivering from this source?" }),
    body: t("delivery.removeMapBody", {
      defaultValue: "Every recipient's portal will show nothing from “{{db}}” until it is mapped again. The mapping is kept for the audit trail.",
      db: row.dbName || row.fieldName,
    }),
    variant: "danger",
    onConfirm: async () => {
      setConfirm(null);
      const done = await run(() => deleteDeliveryFieldMap(token, row.id),
        t("delivery.mapRemoved", { defaultValue: "Source deactivated." }));
      if (done) setMaps((prev) => prev.map((m) => (m.id === row.id ? { ...m, isActive: false } : m)));
    },
  });

  const addRecipient = async (form) => {
    const created = await run(() => createDeliveryRecipient(token, { organization: org, ...form }),
      t("delivery.recipientAdded", { defaultValue: "Recipient created." }));
    if (created) setRecipients((prev) => [created, ...prev]);
  };

  const issueCode = async (row) => {
    const result = await run(() => issueDeliveryAccessCode(token, row.id));
    if (result?.accessCode) {
      setOneTime({ kind: "code", value: result.accessCode, extra: row.displayName });
      setRecipients((prev) => prev.map((r) => (r.id === row.id ? { ...r, hasAccessCode: true, isLocked: false, failedAttempts: 0 } : r)));
    }
  };

  const clearCode = async (row) => {
    const done = await run(() => clearDeliveryAccessCode(token, row.id),
      t("delivery.codeCleared", { defaultValue: "Access code removed." }));
    if (done) setRecipients((prev) => prev.map((r) => (r.id === row.id ? { ...r, hasAccessCode: false } : r)));
  };

  const unlock = async (row) => {
    const updated = await run(() => unlockDeliveryRecipient(token, row.id),
      t("delivery.unlocked", { defaultValue: "Recipient unlocked." }));
    if (updated) setRecipients((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
  };

  const setStatus = async (row, status) => {
    const updated = await run(() => updateDeliveryRecipient(token, row.id, { status }));
    if (updated) setRecipients((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
  };

  const mintGrant = async (form) => {
    const result = await run(() => createDeliveryGrant(token, { organization: org, ...form }));
    if (result?.token) {
      setOneTime({ kind: "link", value: result.link || result.token, extra: result.link ? null : result.token });
      setGrants((prev) => [result.grant, ...prev]);
    }
  };

  const revoke = (row) => setConfirm({
    title: t("delivery.revokeTitle", { defaultValue: "Revoke this delivery?" }),
    body: t("delivery.revokeBody", {
      defaultValue: "The link stops working on the next request, even for someone who already has it open.",
    }),
    variant: "danger",
    onConfirm: async () => {
      setConfirm(null);
      const updated = await run(() => revokeDeliveryGrant(token, row.id, null),
        t("delivery.revoked", { defaultValue: "Delivery revoked." }));
      if (updated) setGrants((prev) => prev.map((g) => (g.id === row.id ? updated : g)));
    },
  });

  const addIssuer = async (form) => {
    const created = await run(() => createDeliveryIssuer(token, { organization: org, ...form }),
      t("delivery.issuerAdded", { defaultValue: "Trusted issuer added." }));
    if (created) setIssuers((prev) => [...prev, created]);
  };

  const removeIssuer = (row) => setConfirm({
    title: t("delivery.removeIssuerTitle", { defaultValue: "Stop trusting this issuer?" }),
    body: t("delivery.removeIssuerBody", {
      defaultValue: "Recipients who sign in through “{{name}}” will no longer be able to. The issuer is kept, deactivated.",
      name: row.name,
    }),
    variant: "danger",
    onConfirm: async () => {
      setConfirm(null);
      const done = await run(() => deleteDeliveryIssuer(token, row.id),
        t("delivery.issuerRemoved", { defaultValue: "Issuer deactivated." }));
      if (done) setIssuers((prev) => prev.map((i) => (i.id === row.id ? { ...i, isActive: false } : i)));
    },
  });

  const activeGrants = grants.filter((g) => g.effectiveStatus === "Active").length;

  return (
    <div className="app-root">
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />

      <div className="page-container" style={{ paddingBottom: 40 }}>
        <Hero
          title={t("delivery.title", { defaultValue: "Delivery & self-service portal" })}
          subtitle={t("delivery.subtitle", {
            defaultValue: "Hand documents to people outside the archive — revocably, time-boxed, and fully audited.",
          })}
          statValue={activeGrants}
          statLabel={t("delivery.activeDeliveries", { defaultValue: "active deliveries" })}
          statDotColor={policy?.enabled ? "#4ade80" : "#94a3b8"}
          onRefresh={load}
          refreshing={loading}
          art={<UsersArt />}
        />

        {error && <div className="asf-alert">⚠ {error}</div>}
        {notice && !error && (
          <div className="asf-alert" style={{ color: "#15803d", background: "#f0fdf4", borderColor: "#bbf7d0" }}>
            ✓ {notice}
          </div>
        )}

        {!policy?.enabled && (
          <div className="asf-card" style={{ borderColor: "#fde68a", background: "#fffbeb" }}>
            <div style={{ fontWeight: 700, color: "#92400e" }}>
              {t("delivery.closedTitle", { defaultValue: "The portal is closed" })}
            </div>
            <div style={{ fontSize: 13, color: "#78350f", marginTop: 6 }}>
              {t("delivery.closedHint", {
                defaultValue:
                  "Nothing here is reachable from outside until you open it. Existing links stop working while it is closed, and start working again when it reopens.",
              })}
            </div>
          </div>
        )}

        <div className="asf-toolbar">
          <SegControl
            value={tab}
            onChange={setTab}
            options={[
              { value: "recipients", label: t("delivery.tabRecipients", { defaultValue: "Recipients" }) },
              { value: "grants", label: t("delivery.tabDeliveries", { defaultValue: "Deliveries" }) },
              { value: "sources", label: t("delivery.tabSources", { defaultValue: "Sources" }) },
              { value: "batch", label: t("delivery.tabBulk", { defaultValue: "Bulk links" }) },
              { value: "branding", label: t("delivery.tabBranding", { defaultValue: "Branding" }) },
              { value: "settings", label: t("delivery.tabSettings", { defaultValue: "Settings" }) },
            ]}
          />
          <span className="asf-count">
            {tab === "recipients" && `${recipients.length} ${t("delivery.recipients", { defaultValue: "recipients" })}`}
            {tab === "grants" && `${grants.length} ${t("delivery.deliveries", { defaultValue: "deliveries" })}`}
            {tab === "sources" && `${maps.filter((m) => m.isActive).length} ${t("delivery.mapped", { defaultValue: "mapped" })}`}
            {tab === "batch" && (policy?.batchLinksEnabled
              ? t("delivery.bulkOnShort", { defaultValue: "on" })
              : t("delivery.bulkOffShort", { defaultValue: "off" }))}
            {tab === "branding" && (org || "—")}
            {tab === "settings" && (org || "—")}
          </span>
          {/* The master switch lives here rather than on the gradient: the hero already carries the
              count and the refresh button, and this keeps it reachable from every tab. */}
          <ToggleSwitch
            checked={!!policy?.enabled}
            onChange={togglePortal}
            label={t("delivery.portalEnabled", { defaultValue: "Portal open" })}
          />
        </div>

        {tab === "recipients" && (
          <RecipientsTab
            t={t} busy={busy} recipients={recipients} databases={databases} mappedDbIds={mappedDbIds}
            issuers={issuers.filter((i) => i.isActive)} token={token}
            onAdd={addRecipient} onIssueCode={issueCode} onClearCode={clearCode}
            onUnlock={unlock} onStatus={setStatus} onError={fail}
          />
        )}

        {tab === "grants" && (
          <GrantsTab
            t={t} busy={busy} grants={grants} recipients={recipients.filter((r) => r.status === "Active")}
            databases={databases.filter((d) => mappedDbIds.has(dbId(d)))} policy={policy}
            onMint={mintGrant} onRevoke={revoke}
          />
        )}

        {tab === "sources" && (
          <SourcesTab t={t} busy={busy} maps={maps} databases={databases} token={token}
            onAdd={addMap} onRemove={removeMap} onError={setError} onNotice={setNotice} />
        )}

        {tab === "batch" && (
          <BatchTab
            t={t} busy={busy} policy={policy}
            databases={databases.filter((d) => mappedDbIds.has(dbId(d)))}
            runs={batchRuns} revocations={batchRevocations}
            token={token} onSavePolicy={savePolicyFields} onReload={loadBatch}
            onRevokeKey={revokeBatchKey} onRevokeAll={revokeBatchAll}
            onError={fail} onNotice={setNotice}
          />
        )}

        {/* Branding owns its own row and loads it itself, so it neither waits on nor reloads the rest of
            the page. Nothing it changes can affect a scope, a grant or a link. */}
        {tab === "branding" && (
          <BrandingTab
            t={t} busy={busy} token={token} organization={org}
            onError={fail} onNotice={setNotice}
          />
        )}

        {tab === "settings" && (
          <SettingsTab
            t={t} busy={busy} policy={policy} issuers={issuers} algorithms={algorithms}
            onSave={savePolicyFields} onAddIssuer={addIssuer} onRemoveIssuer={removeIssuer}
          />
        )}
      </div>

      {oneTime && <OneTimeSecret t={t} secret={oneTime} onClose={() => setOneTime(null)} />}

      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        body={confirm?.body}
        variant={confirm?.variant || "warn"}
        icon={confirm?.icon || "⚠"}
        confirmLabel={t("common.confirm", { defaultValue: "Confirm" })}
        onConfirm={confirm?.onConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

/* ── One-time secret ─────────────────────────────────────────────────────────────────
   The access code and the delivery link are shown here and nowhere else, ever. The dialog
   is deliberately blunt about that, and offers copy-to-clipboard so nobody retypes a
   30-character token by eye. */
function OneTimeSecret({ t, secret, onClose }) {
  const [copied, setCopied] = useState(false);
  const isCode = secret.kind === "code";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret.value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked — the value is on screen to select by hand */ }
  };

  return (
    <div className="dbModalOverlay" role="dialog" aria-modal="true">
      <div className="dbModalCard dbModalConfirmCard" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="dbModalConfirmBadge dbModalConfirmBadge--warn">{isCode ? "🔑" : "🔗"}</div>

        <div className="dbModalTitle">
          {isCode
            ? t("delivery.codeTitle", { defaultValue: "Access code" })
            : t("delivery.linkTitle", { defaultValue: "Delivery link" })}
        </div>

        <p style={{ fontSize: 13, color: "#475569", margin: "0 0 14px" }}>
          {isCode
            ? t("delivery.codeFor", { defaultValue: "For {{name}}. Hand it over through a channel you trust.", name: secret.extra })
            : t("delivery.linkHint", { defaultValue: "Send this to the recipient yourself — nothing is sent from here." })}
        </p>

        <div style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: isCode ? 22 : 13,
          fontWeight: isCode ? 700 : 500, letterSpacing: isCode ? "0.12em" : 0, wordBreak: "break-all",
          background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 18px", color: "#0f172a",
        }}>
          {secret.value}
        </div>

        {secret.extra && !isCode && (
          <p style={{ fontSize: 12, color: "#64748b" }}>
            {t("delivery.noBaseUrl", {
              defaultValue: "No portal address is configured, so this is the raw token. Set ApplicationSettings:Portal_BaseUrl to get a ready-made link.",
            })}
          </p>
        )}

        <div className="dbModalConfirmBanner dbModalConfirmBanner--warn" style={{ marginTop: 14 }}>
          <p>
            {t("delivery.onceOnly", {
              defaultValue: "This is shown once. Only a hash was stored, so it cannot be retrieved again — if it is lost, issue a new one.",
            })}
          </p>
        </div>

        <div className="dbModalConfirmActions">
          <button type="button" className="dbModalCancel" onClick={copy}>
            {copied ? t("common.copied", { defaultValue: "Copied" }) : t("common.copy", { defaultValue: "Copy" })}
          </button>
          <button type="button" className="dbModalConfirmBtn dbModalConfirmBtn--warn" onClick={onClose}>
            {t("delivery.savedIt", { defaultValue: "I've saved it" })}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Recipients ──────────────────────────────────────────────────────────────────── */
function RecipientsTab({ t, busy, recipients, databases, mappedDbIds, issuers, token, onAdd, onIssueCode, onClearCode, onUnlock, onStatus, onError }) {
  const [form, setForm] = useState({ displayName: "", externalRef: "", email: "", phone: "", trustedIssuerId: "" });
  const [expanded, setExpanded] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    await onAdd({
      displayName: form.displayName,
      externalRef: form.externalRef || null,
      email: form.email || null,
      phone: form.phone || null,
      trustedIssuerId: form.trustedIssuerId ? Number(form.trustedIssuerId) : null,
    });
    setForm({ displayName: "", externalRef: "", email: "", phone: "", trustedIssuerId: "" });
  };

  return (
    <>
      <form className="asf-card" onSubmit={submit}>
        <div className="asf-grid">
          <div className="asf-field" style={{ flex: "1 1 200px" }}>
            <label className="asf-lbl">{t("delivery.name", { defaultValue: "Name" })}</label>
            <input className="asf-input" value={form.displayName} required
              onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
          </div>
          <div className="asf-field" style={{ flex: "1 1 180px" }}>
            <label className="asf-lbl">{t("delivery.reference", { defaultValue: "Customer reference" })}</label>
            <input className="asf-input" value={form.externalRef} placeholder="CUST-100244"
              onChange={(e) => setForm({ ...form, externalRef: e.target.value })} />
          </div>
          <div className="asf-field" style={{ flex: "1 1 180px" }}>
            <label className="asf-lbl">{t("delivery.email", { defaultValue: "Email" })}</label>
            <input className="asf-input" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          {issuers.length > 0 && (
            <div className="asf-field" style={{ flex: "1 1 180px" }}>
              <label className="asf-lbl">{t("delivery.signsInVia", { defaultValue: "Signs in via" })}</label>
              <Dropdown value={form.trustedIssuerId}
                onChange={(v) => setForm({ ...form, trustedIssuerId: v })}
                options={[
                  { value: "", label: t("delivery.accessCodeOption", { defaultValue: "Access code" }) },
                  ...issuers.map((i) => ({ value: String(i.id), label: i.name })),
                ]} />
            </div>
          )}
          <button className="asf-cta" type="submit" disabled={busy || !form.displayName.trim()}>
            {t("delivery.addRecipient", { defaultValue: "Add recipient" })}
          </button>
        </div>
        <p style={{ fontSize: 12, color: "#64748b", margin: "12px 0 0" }}>
          {t("delivery.emailNotUsed", {
            defaultValue: "Contact details are stored for your reference only — this system never sends anything to them.",
          })}
        </p>
      </form>

      {recipients.length === 0 ? (
        <EmptyState icon="👤" title={t("delivery.noRecipients", { defaultValue: "No recipients yet" })}
          hint={t("delivery.noRecipientsHint", { defaultValue: "Add the people who should be able to fetch their own documents." })} />
      ) : (
        <div className="asf-tablecard">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #eef0f5" }}>
                <Th>{t("delivery.name", { defaultValue: "Name" })}</Th>
                <Th>{t("delivery.reference", { defaultValue: "Customer reference" })}</Th>
                <Th>{t("delivery.keys", { defaultValue: "Keys" })}</Th>
                <Th>{t("delivery.signIn", { defaultValue: "Sign-in" })}</Th>
                <Th>{t("delivery.lastSeen", { defaultValue: "Last seen" })}</Th>
                <Th align="right">{t("common.actions", { defaultValue: "Actions" })}</Th>
              </tr>
            </thead>
            <tbody>
              {recipients.map((r) => (
                <React.Fragment key={r.id}>
                  <tr className="asf-row" style={{ borderBottom: "1px solid #f4f5f8" }}>
                    <Td>
                      <b>{r.displayName}</b>
                      {r.status !== "Active" && (
                        <span className="asf-status asf-status--off" style={{ marginLeft: 8 }}>
                          <i className="asf-status__dot" />{t("delivery.disabled", { defaultValue: "Disabled" })}
                        </span>
                      )}
                      {r.isLocked && (
                        <span className="asf-status asf-status--err" style={{ marginLeft: 8 }}>
                          <i className="asf-status__dot" />{t("delivery.locked", { defaultValue: "Locked" })}
                        </span>
                      )}
                    </Td>
                    <Td>{r.externalRef || "—"}</Td>
                    <Td>
                      <button className="asf-chip" style={{ cursor: "pointer" }}
                        onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                        {r.activeKeys} ▾
                      </button>
                    </Td>
                    <Td>
                      {r.trustedIssuerId
                        ? t("delivery.viaIssuer", { defaultValue: "Delegated" })
                        : r.hasAccessCode
                          ? t("delivery.viaCode", { defaultValue: "Access code" })
                          : t("delivery.linkOnly", { defaultValue: "Link only" })}
                    </Td>
                    <Td>{fmtAbs(r.lastSeenAt)}</Td>
                    <Td align="right">
                      {!r.trustedIssuerId && (
                        <button className="asf-btn" style={{ padding: "6px 12px", fontSize: 12.5 }}
                          disabled={busy} onClick={() => onIssueCode(r)}>
                          {r.hasAccessCode
                            ? t("delivery.newCode", { defaultValue: "New code" })
                            : t("delivery.issueCode", { defaultValue: "Issue code" })}
                        </button>
                      )}
                      {r.hasAccessCode && (
                        <button className="asf-btn" style={{ padding: "6px 12px", fontSize: 12.5, marginLeft: 6 }}
                          disabled={busy} onClick={() => onClearCode(r)}>
                          {t("delivery.clearCode", { defaultValue: "Clear code" })}
                        </button>
                      )}
                      {r.isLocked && (
                        <button className="asf-btn" style={{ padding: "6px 12px", fontSize: 12.5, marginLeft: 6 }}
                          disabled={busy} onClick={() => onUnlock(r)}>
                          {t("delivery.unlock", { defaultValue: "Unlock" })}
                        </button>
                      )}
                      <button className="asf-btn" style={{ padding: "6px 12px", fontSize: 12.5, marginLeft: 6 }}
                        disabled={busy} onClick={() => onStatus(r, r.status === "Active" ? "Disabled" : "Active")}>
                        {r.status === "Active"
                          ? t("delivery.disable", { defaultValue: "Disable" })
                          : t("delivery.enable", { defaultValue: "Enable" })}
                      </button>
                    </Td>
                  </tr>
                  {expanded === r.id && (
                    <tr>
                      <td colSpan={6} style={{ background: "#faf9ff", padding: "14px 18px" }}>
                        <KeysEditor t={t} token={token} recipient={r} databases={databases}
                          mappedDbIds={mappedDbIds} onError={onError} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/**
 * The recipient's keys — the values that identify them in the document data, and therefore the entire
 * boundary of what they can see. Loaded lazily when a row is expanded, since most rows are never opened.
 */
function KeysEditor({ t, token, recipient, databases, mappedDbIds, onError }) {
  const [keys, setKeys] = useState(null);
  const [value, setValue] = useState("");
  const [scopeDb, setScopeDb] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try { setKeys((await getDeliveryRecipientKeys(token, recipient.id))?.keys || []); }
    catch (e) { onError(e); setKeys([]); }
  }, [token, recipient.id, onError]);

  useEffect(() => { reload(); }, [reload]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await addDeliveryRecipientKey(token, recipient.id, {
        keyValue: value.trim(),
        dbInfoId: scopeDb ? Number(scopeDb) : null,
      });
      setValue("");
      await reload();
    } catch (err) { onError(err); } finally { setBusy(false); }
  };

  const remove = async (k) => {
    setBusy(true);
    try { await deleteDeliveryRecipientKey(token, k.id); await reload(); }
    catch (err) { onError(err); } finally { setBusy(false); }
  };

  const mapped = databases.filter((d) => mappedDbIds.has(dbId(d)));

  return (
    <div>
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>
        {t("delivery.keysHint", {
          defaultValue: "These values are matched against the mapped column. Whatever is listed here is exactly what this person can see.",
        })}
      </div>
      <form onSubmit={add} style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12 }}>
        <div className="asf-field" style={{ flex: "1 1 180px" }}>
          <label className="asf-lbl">{t("delivery.keyValue", { defaultValue: "Key value" })}</label>
          <input className="asf-input" value={value} onChange={(e) => setValue(e.target.value)} required />
        </div>
        <div className="asf-field" style={{ flex: "1 1 180px" }}>
          <label className="asf-lbl">{t("delivery.keyScope", { defaultValue: "Applies to" })}</label>
          <Dropdown value={scopeDb} onChange={setScopeDb}
            options={[
              { value: "", label: t("delivery.allSources", { defaultValue: "All mapped sources" }) },
              ...mapped.map((d) => ({ value: String(dbId(d)), label: dbNameOf(d) })),
            ]} />
        </div>
        <button className="asf-btn" type="submit" disabled={busy || !value.trim()}>
          {t("delivery.addKey", { defaultValue: "Add key" })}
        </button>
      </form>
      {keys === null ? (
        <div style={{ color: "#94a3b8", fontSize: 12.5 }}>{t("common.loading", { defaultValue: "Loading…" })}</div>
      ) : keys.length === 0 ? (
        <div style={{ color: "#b45309", fontSize: 12.5 }}>
          {t("delivery.noKeys", { defaultValue: "No keys — this recipient can see nothing until one is added." })}
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {keys.map((k) => (
            <span key={k.id} className="asf-chip" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <code>{k.keyValue}</code>
              <span style={{ color: "#8b7bb8" }}>{k.dbName || t("delivery.allSourcesShort", { defaultValue: "all" })}</span>
              <button onClick={() => remove(k)} disabled={busy}
                style={{ border: "none", background: "none", cursor: "pointer", color: "#7c3aed", fontWeight: 700 }}>×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Deliveries (grants) ─────────────────────────────────────────────────────────── */
function GrantsTab({ t, busy, grants, recipients, databases, policy, onMint, onRevoke }) {
  const maxDays = Math.max(1, Math.floor((policy?.maxValiditySeconds || 30 * DAY) / DAY));
  const [form, setForm] = useState({ recipientId: "", dbInfoId: "", label: "", days: "", maxRedemptions: "", requireAccessCode: false });

  const submit = async (e) => {
    e.preventDefault();
    await onMint({
      recipientId: form.recipientId ? Number(form.recipientId) : null,
      scopeKind: "Recipient",
      dbInfoId: form.dbInfoId ? Number(form.dbInfoId) : null,
      label: form.label || null,
      validitySeconds: form.days ? Number(form.days) * DAY : null,
      maxRedemptions: form.maxRedemptions ? Number(form.maxRedemptions) : null,
      requireAccessCode: form.requireAccessCode,
    });
    setForm({ recipientId: "", dbInfoId: "", label: "", days: "", maxRedemptions: "", requireAccessCode: false });
  };

  return (
    <>
      <form className="asf-card" onSubmit={submit}>
        <div className="asf-grid">
          <div className="asf-field" style={{ flex: "1 1 200px" }}>
            <label className="asf-lbl">{t("delivery.recipient", { defaultValue: "Recipient" })}</label>
            <Dropdown value={form.recipientId}
              onChange={(v) => setForm({ ...form, recipientId: v })}
              placeholder={t("common.select", { defaultValue: "Select…" })}
              options={recipients.map((r) => ({ value: String(r.id), label: r.displayName }))} />
          </div>
          <div className="asf-field" style={{ flex: "1 1 170px" }}>
            <label className="asf-lbl">{t("delivery.source", { defaultValue: "Source" })}</label>
            <Dropdown value={form.dbInfoId}
              onChange={(v) => setForm({ ...form, dbInfoId: v })}
              options={[
                { value: "", label: t("delivery.allSources", { defaultValue: "All mapped sources" }) },
                ...databases.map((d) => ({ value: String(dbId(d)), label: dbNameOf(d) })),
              ]} />
          </div>
          <div className="asf-field" style={{ flex: "1 1 150px" }}>
            <label className="asf-lbl">{t("delivery.label", { defaultValue: "Label" })}</label>
            <input className="asf-input" value={form.label} placeholder={t("delivery.labelPh", { defaultValue: "March statements" })}
              onChange={(e) => setForm({ ...form, label: e.target.value })} />
          </div>
          <div className="asf-field" style={{ flex: "0 1 110px" }}>
            <label className="asf-lbl">{t("delivery.validDays", { defaultValue: "Valid (days)" })}</label>
            <input className="asf-input" type="number" min="1" max={maxDays} value={form.days}
              placeholder={String(Math.floor((policy?.defaultValiditySeconds || 7 * DAY) / DAY))}
              onChange={(e) => setForm({ ...form, days: e.target.value })} />
          </div>
          <div className="asf-field" style={{ flex: "0 1 110px" }}>
            <label className="asf-lbl">{t("delivery.maxOpens", { defaultValue: "Max opens" })}</label>
            <input className="asf-input" type="number" min="1" value={form.maxRedemptions}
              placeholder="∞" onChange={(e) => setForm({ ...form, maxRedemptions: e.target.value })} />
          </div>
          <div className="asf-field" style={{ flex: "0 1 auto", paddingBottom: 10 }}>
            <ToggleSwitch checked={form.requireAccessCode}
              onChange={(v) => setForm({ ...form, requireAccessCode: v })}
              label={t("delivery.requireCode", { defaultValue: "Require code" })} />
          </div>
          <button className="asf-cta" type="submit" disabled={busy || !form.recipientId || !policy?.enabled}>
            {t("delivery.createLink", { defaultValue: "Create link" })}
          </button>
        </div>
      </form>

      {grants.length === 0 ? (
        <EmptyState icon="🔗" title={t("delivery.noGrants", { defaultValue: "No deliveries yet" })}
          hint={t("delivery.noGrantsHint", { defaultValue: "Create a link and hand it to the recipient through your own channel." })} />
      ) : (
        <div className="asf-tablecard">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #eef0f5" }}>
                <Th>{t("delivery.label", { defaultValue: "Label" })}</Th>
                <Th>{t("delivery.recipient", { defaultValue: "Recipient" })}</Th>
                <Th>{t("delivery.scope", { defaultValue: "Scope" })}</Th>
                <Th>{t("delivery.expires", { defaultValue: "Expires" })}</Th>
                <Th>{t("delivery.opens", { defaultValue: "Opens" })}</Th>
                <Th>{t("common.status", { defaultValue: "Status" })}</Th>
                <Th align="right">{t("common.actions", { defaultValue: "Actions" })}</Th>
              </tr>
            </thead>
            <tbody>
              {grants.map((g) => (
                <tr key={g.id} className="asf-row" style={{ borderBottom: "1px solid #f4f5f8" }}>
                  <Td>{g.label || "—"}</Td>
                  <Td>{g.recipientName || (g.deliveryRecipientId ? `#${g.deliveryRecipientId}` : t("delivery.bearer", { defaultValue: "Bearer" }))}</Td>
                  <Td>
                    {g.scopeKind === "DocumentSet"
                      ? t("delivery.nDocuments", { defaultValue: "{{count}} documents", count: g.documentCount })
                      : (g.dbName || t("delivery.allSources", { defaultValue: "All mapped sources" }))}
                    {g.requiresAccessCode && <span className="asf-chip" style={{ marginLeft: 8 }}>🔒</span>}
                  </Td>
                  <Td>{fmtAbs(g.expiresAt)}</Td>
                  <Td>{g.redemptionCount}{g.maxRedemptions ? ` / ${g.maxRedemptions}` : ""}</Td>
                  <Td>
                    <span className={`asf-status asf-status--${g.effectiveStatus === "Active" ? "on" : g.effectiveStatus === "Revoked" ? "err" : "off"}`}>
                      <i className="asf-status__dot" />
                      {g.effectiveStatus === "Active" ? t("delivery.statusActive", { defaultValue: "Active" })
                        : g.effectiveStatus === "Revoked" ? t("delivery.statusRevoked", { defaultValue: "Revoked" })
                        : t("delivery.statusExpired", { defaultValue: "Expired" })}
                    </span>
                  </Td>
                  <Td align="right">
                    {g.effectiveStatus === "Active" && (
                      <button className="asf-btn" style={{ padding: "6px 12px", fontSize: 12.5 }}
                        disabled={busy} onClick={() => onRevoke(g)}>
                        {t("delivery.revoke", { defaultValue: "Revoke" })}
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ── Sources (recipient-key field maps) ──────────────────────────────────────────── */
/** A base's active index fields, as the pickers want them. One derivation, so the Sources tab, the
    run wizard and the answer editor can never disagree about what a base holds. */
function columnsOf(databases, id) {
  const chosen = (databases || []).find((d) => String(dbId(d)) === String(id));
  return (chosen?.relatedIndexes || chosen?.RelatedIndexes || [])
    .filter((f) => (f.isActive ?? f.IsActive ?? true))
    .map((f) => ({ name: f.fieldName ?? f.FieldName, display: f.fieldDisplayName ?? f.FieldDisplayName }))
    .filter((c) => !!c.name);
}

export function SourcesTab({ t, busy, maps, databases, token, onAdd, onRemove, onError, onNotice }) {
  const [form, setForm] = useState({ dbInfoId: "", fieldName: "", matchMode: "Exact" });

  // The verification question belongs to the SOURCE, not to a run, and it is resolved at redemption —
  // so setting one here protects links that were printed and posted months ago. That is the whole point
  // of it, and it was unreachable while the editor lived only inside the create-a-run wizard: an
  // operator wanting to protect what they had already sent had to start a run they did not want.
  const [questions, setQuestions] = useState({});   // dbInfoId → the saved question, or null
  const [openId, setOpenId] = useState(null);

  const loadQuestion = useCallback(async (id) => {
    try {
      const v = await getDeliveryVerification(token, id);
      setQuestions((q) => ({ ...q, [id]: v && v.configured ? v : null }));
    } catch {
      // A base with no question is the normal case, not an error.
      setQuestions((q) => ({ ...q, [id]: null }));
    }
  }, [token]);

  useEffect(() => {
    maps.filter((m) => m.isActive).forEach((m) => loadQuestion(m.dbInfoId));
  }, [maps, loadQuestion]);

  // The columns are already on the base we were given — asking the operator to TYPE a column name was
  // the reason nothing ever got mapped. Nobody knows the physical column names by heart, a typo is only
  // discovered when the portal shows a customer nothing, and there is no list to check against.
  const chosen = databases.find((d) => String(dbId(d)) === String(form.dbInfoId));
  const columns = (chosen?.relatedIndexes || chosen?.RelatedIndexes || [])
    .filter((f) => (f.isActive ?? f.IsActive ?? true))
    .map((f) => ({
      name: f.fieldName ?? f.FieldName,
      display: f.fieldDisplayName ?? f.FieldDisplayName,
      indexed: String(f.dbIndexStatus ?? f.DbIndexStatus ?? "").toLowerCase() === "active",
    }))
    .filter((c) => !!c.name);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.fieldName) return;
    await onAdd({ dbInfoId: Number(form.dbInfoId), fieldName: form.fieldName.trim(), matchMode: form.matchMode });
    setForm({ dbInfoId: "", fieldName: "", matchMode: "Exact" });
  };

  return (
    <>
      <form className="asf-card" onSubmit={submit}>
        <div style={{ fontSize: 13, color: "#475569", marginBottom: 14 }}>
          {t("delivery.sourcesHint", {
            defaultValue: "Say which column holds the recipient's own identifier in each document base. Without a mapping, a base is invisible to the portal.",
          })}
        </div>
        <div className="asf-grid">
          <div className="asf-field" style={{ flex: "1 1 200px" }}>
            <label className="asf-lbl">{t("delivery.database", { defaultValue: "Document base" })}</label>
            <Dropdown value={form.dbInfoId}
              onChange={(v) => setForm({ ...form, dbInfoId: v, fieldName: "" })}
              placeholder={t("common.select", { defaultValue: "Select…" })}
              options={databases.map((d) => ({ value: String(dbId(d)), label: dbNameOf(d) }))} />
          </div>
          <div className="asf-field" style={{ flex: "1 1 240px" }}>
            <label className="asf-lbl">{t("delivery.keyColumn", { defaultValue: "Recipient key column" })}</label>
            <Dropdown value={form.fieldName}
              onChange={(v) => setForm({ ...form, fieldName: v })}
              placeholder={form.dbInfoId
                ? t("common.select", { defaultValue: "Select…" })
                : t("delivery.pickBaseFirst", { defaultValue: "Choose a document base first" })}
              emptyLabel={form.dbInfoId
                ? t("delivery.noColumns", { defaultValue: "This base has no index fields" })
                : t("delivery.pickBaseFirst", { defaultValue: "Choose a document base first" })}
              options={columns.map((c) => ({
                value: c.name,
                // The ✓ marks a column that already has a real database index behind it. Choosing one of
                // those is the difference between a portal that answers instantly and one that reads the
                // whole archive on every customer visit, and this is the moment the choice is made.
                label: `${c.display && c.display !== c.name ? `${c.display} (${c.name})` : c.name}${c.indexed ? "  ✓" : ""}`,
              }))} />
            {form.dbInfoId && columns.length > 0 && (
              <span style={{ fontSize: 12, color: "#64748b", marginTop: 4, display: "block" }}>
                {t("delivery.keyColumnHint", {
                  defaultValue: "✓ marks columns that are already indexed — those keep the portal fast.",
                })}
              </span>
            )}
            {form.dbInfoId && columns.length === 0 && (
              <span style={{ fontSize: 12, color: "#92400e", marginTop: 4, display: "block" }}>
                {t("delivery.noColumnsHint", {
                  defaultValue: "This base has no index fields defined, so there is no column to deliver by. Add one under Admin → Index Fields.",
                })}
              </span>
            )}
          </div>
          <div className="asf-field" style={{ flex: "0 1 180px" }}>
            <label className="asf-lbl">{t("delivery.matchMode", { defaultValue: "Match" })}</label>
            <Dropdown value={form.matchMode}
              onChange={(v) => setForm({ ...form, matchMode: v })}
              options={[
                { value: "Exact", label: t("delivery.modeExact", { defaultValue: "Exact" }) },
                { value: "EqualsIgnoreCase", label: t("delivery.modeIgnoreCase", { defaultValue: "Ignore case" }) },
                { value: "Contains", label: t("delivery.modeContains", { defaultValue: "Contains (risky)" }) },
              ]} />
          </div>
          <button className="asf-cta" type="submit" disabled={busy || !form.dbInfoId || !form.fieldName.trim()}>
            {t("delivery.mapColumn", { defaultValue: "Map column" })}
          </button>
        </div>
        {form.matchMode === "Contains" && (
          <div style={{ marginTop: 12, fontSize: 12.5, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "10px 12px" }}>
            ⚠ {t("delivery.containsWarning", {
              defaultValue: "“Contains” can match a different customer: key 1234 is a substring of 912345. Use it only where the key format cannot be part of another.",
            })}
          </div>
        )}
      </form>

      {maps.length === 0 ? (
        <EmptyState icon="🗂" title={t("delivery.noMaps", { defaultValue: "No sources mapped" })}
          hint={t("delivery.noMapsHint", { defaultValue: "The portal has nothing to show until a recipient-key column is mapped." })} />
      ) : (
        <div className="asf-tablecard">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #eef0f5" }}>
                <Th>{t("delivery.database", { defaultValue: "Document base" })}</Th>
                <Th>{t("delivery.keyColumn", { defaultValue: "Recipient key column" })}</Th>
                <Th>{t("delivery.matchMode", { defaultValue: "Match" })}</Th>
                <Th>{t("common.status", { defaultValue: "Status" })}</Th>
                <Th>{t("delivery.question", { defaultValue: "Question asked" })}</Th>
                <Th align="right">{t("common.actions", { defaultValue: "Actions" })}</Th>
              </tr>
            </thead>
            <tbody>
              {maps.map((m) => (
                <tr key={m.id} className="asf-row" style={{ borderBottom: "1px solid #f4f5f8" }}>
                  <Td><b>{m.dbName || `#${m.dbInfoId}`}</b></Td>
                  <Td><code>{m.fieldName}</code></Td>
                  <Td>{m.matchMode}</Td>
                  <Td>
                    <span className={`asf-status asf-status--${m.isActive ? "on" : "off"}`}>
                      <i className="asf-status__dot" />
                      {m.isActive ? t("delivery.active", { defaultValue: "Active" }) : t("delivery.inactive", { defaultValue: "Inactive" })}
                    </span>
                  </Td>
                  <Td>
                    {questions[m.dbInfoId]
                      ? <span title={questions[m.dbInfoId].template || questions[m.dbInfoId].fieldName}>
                          🔒 {questions[m.dbInfoId].prompt || questions[m.dbInfoId].template || questions[m.dbInfoId].fieldName}
                        </span>
                      : <span style={{ color: "#94a3b8" }}>
                          {t("delivery.questionNone", { defaultValue: "Anyone with the link can open it" })}
                        </span>}
                  </Td>
                  <Td align="right">
                    {m.isActive && (
                      <>
                        <button className="asf-btn" style={{ padding: "6px 12px", fontSize: 12.5, marginRight: 8 }}
                          onClick={() => setOpenId(openId === m.dbInfoId ? null : m.dbInfoId)}>
                          {openId === m.dbInfoId
                            ? t("common.close", { defaultValue: "Close" })
                            : questions[m.dbInfoId]
                              ? t("delivery.questionChange", { defaultValue: "Change question" })
                              : t("delivery.questionSet", { defaultValue: "Ask a question" })}
                        </button>
                        <button className="asf-btn" style={{ padding: "6px 12px", fontSize: 12.5 }}
                          disabled={busy} onClick={() => onRemove(m)}>
                          {t("delivery.deactivate", { defaultValue: "Deactivate" })}
                        </button>
                      </>
                    )}
                  </Td>
                </tr>
              ))}
              {maps.filter((m) => m.isActive && openId === m.dbInfoId).map((m) => (
                <tr key={`q${m.id}`} style={{ borderBottom: "1px solid #f4f5f8" }}>
                  <td colSpan={6} style={{ padding: "4px 12px 18px", background: "#fcfcfd" }}>
                    <SourceQuestion t={t} token={token} dbInfoId={m.dbInfoId}
                      columns={columnsOf(databases, m.dbInfoId)}
                      current={questions[m.dbInfoId]}
                      onSaved={() => loadQuestion(m.dbInfoId)}
                      onError={onError} onNotice={onNotice} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ── Settings + trusted issuers ──────────────────────────────────────────────────── */
/* ── Bulk links ──────────────────────────────────────────────────────────────────────
   One link per customer for a whole statement run, created before the statements are
   archived.

   The screen is deliberately organised around the two jobs an operator comes here for —
   making this month's links, and looking at what already exists — behind a left rail, and
   never around the mechanism. An operator does not need to know that the links carry no
   database row, that revocation works by raising a fence, or what an epoch is. Those facts
   shape what the buttons DO; none of them belongs on the page.

   The status strip stays above the rail because it answers "what is switched on" for both
   panes, not for one of them.

   Everything that can destroy something lives at the bottom, behind a disclosure. The
   earlier version put "revoke everything" beside Save, above the fold. */
export function BatchTab({ t, busy, policy, databases, runs, revocations, token,
                          onSavePolicy, onReload, onRevokeKey, onRevokeAll, onError, onNotice }) {
  const enabled = !!policy?.batchLinksEnabled;
  const blocked = revocations?.revocations || [];

  if (!enabled) {
    return (
      <div className="asf-card">
        <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>
          {t("delivery.bulkOffTitle", { defaultValue: "Bulk links are turned off" })}
        </div>
        <p style={{ fontSize: 13, color: "#475569", marginBottom: 6 }}>
          {t("delivery.bulkWhat", {
            defaultValue:
              "Bulk links let you create one portal link per customer for a whole statement run — before the " +
              "statements are archived. Your print system puts the link on the statement; the customer opens " +
              "their own documents with it. Nothing is emailed from here.",
          })}
        </p>
        <p style={{ fontSize: 13, color: "#475569", marginBottom: 14 }}>
          {t("delivery.bulkOffWhy", {
            defaultValue:
              "They are turned on separately from the portal because they work differently: a bulk link cannot " +
              "be cancelled on its own — you block a customer, or you block every link at once.",
          })}
        </p>
        <button className="asf-cta" disabled={busy}
          onClick={() => onSavePolicy({ batchLinksEnabled: true })}>
          {t("delivery.bulkTurnOn", { defaultValue: "Turn bulk links on" })}
        </button>
      </div>
    );
  }

  return (
    <>
      <BulkStatus t={t} policy={policy} blockedCount={blocked.length} />
      <BulkSplit t={t} busy={busy} policy={policy} databases={databases} runs={runs} blocked={blocked}
        token={token} onReload={onReload} onRevokeKey={onRevokeKey} onSavePolicy={onSavePolicy}
        onRevokeAll={onRevokeAll} onError={onError} onNotice={onNotice} />
    </>
  );
}

/* The tab carries three jobs that share nothing but a subject: making this month's links,
   looking at what already exists, and setting the rules everything is minted under. Stacked,
   the create wizard pushed the tables so far down that an operator checking a blocked customer
   scrolled past three steps to reach them. The rail shows one job at a time and keeps the
   counts in view, so nothing the switch hides is a surprise.

   Advanced is a pane rather than a disclosure now: the rail already keeps it out of the way of
   the daily job, and a panel that had to be unfolded before it would say what the tenant was
   configured for was hiding the answer, not the danger. What is actually dangerous in there is
   still separated inside the pane and still asks before it fires. */
function BulkSplit({ t, busy, policy, databases, runs, blocked, token,
                     onReload, onRevokeKey, onSavePolicy, onRevokeAll, onError, onNotice }) {
  const [pane, setPane] = useState("create");
  const runCount = runs?.length || 0;

  const item = (id, ico, label, badges = null) => (
    <button type="button" className={`asf-navitem${pane === id ? " on" : ""}`}
      aria-current={pane === id ? "page" : undefined} onClick={() => setPane(id)}>
      <span className="asf-navitem__ico" aria-hidden="true">{ico}</span>
      <span className="asf-navitem__lbl">{label}</span>
      {badges}
    </button>
  );

  return (
    <div className="asf-split">
      <nav className="asf-split__side">
        {item("create", "✦", t("delivery.bulkPaneCreate", { defaultValue: "New links" }))}
        {item("existing", "🗂", t("delivery.bulkPaneExisting", { defaultValue: "Recent & blocked" }), (
          <>
            {blocked.length > 0 && (
              <span className="asf-navitem__badge asf-navitem__badge--warn"
                title={t("delivery.bulkSomeBlocked", { count: blocked.length, defaultValue: "{{count}} customers blocked" })}>
                {blocked.length}
              </span>
            )}
            {runCount > 0 && <span className="asf-navitem__badge">{runCount}</span>}
          </>
        ))}
        {item("advanced", "⚙", t("delivery.bulkAdvanced", { defaultValue: "Advanced" }))}
      </nav>

      <div className="asf-split__main">
        {pane === "create" && (
          <BulkCreate t={t} busy={busy} databases={databases} token={token}
            onError={onError} onNotice={onNotice} onCreated={onReload} />
        )}
        {pane === "existing" && (
          <>
            <BulkHistory t={t} runs={runs} />
            <BulkBlocked t={t} busy={busy} blocked={blocked} onRevokeKey={onRevokeKey} />
          </>
        )}
        {pane === "advanced" && (
          <BulkAdvanced t={t} busy={busy} policy={policy} onSavePolicy={onSavePolicy} onRevokeAll={onRevokeAll} />
        )}
      </div>
    </div>
  );
}

/* A plain-language answer to "what is switched on right now", so an operator does not have to
   infer it from a number. */
function BulkStatus({ t, policy, blockedCount }) {
  const MONTH = 2628000;
  const secs = policy?.batchLinkLifetimeSeconds;
  const life = secs == null
    ? t("delivery.bulkLifeNever", { defaultValue: "Links never expire" })
    : t("delivery.bulkLifeMonths", { count: Math.round(secs / MONTH), defaultValue: "Links expire after {{count}} months" });

  return (
    <div className="asf-card" style={{ display: "flex", gap: 22, flexWrap: "wrap", alignItems: "center" }}>
      <span className="asf-status asf-status--on"><i className="asf-status__dot" />
        {t("delivery.bulkOn", { defaultValue: "Bulk links are on" })}
      </span>
      <span style={{ fontSize: 13, color: "#475569" }}>{life}</span>
      <span style={{ fontSize: 13, color: blockedCount ? "#b91c1c" : "#475569" }}>
        {blockedCount === 0
          ? t("delivery.bulkNoneBlocked", { defaultValue: "No customers blocked" })
          : t("delivery.bulkSomeBlocked", { count: blockedCount, defaultValue: "{{count}} customers blocked" })}
      </span>
    </div>
  );
}

/* The whole job, as three steps. */
function BulkCreate({ t, busy, databases, token, onError, onNotice, onCreated }) {
  const [form, setForm] = useState({ dbInfoId: "", period: "", label: "", keys: "", formats: "PDF" });
  const [protect, setProtect] = useState({ source: "None", normalizePunctuation: true });
  const [readiness, setReadiness] = useState(null);
  const [checking, setChecking] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const dbInfoId = Number(form.dbInfoId) || 0;

  const columns = columnsOf(databases, form.dbInfoId);

  // A question already saved for this base is shown as the current choice rather than silently ignored —
  // otherwise an operator would set one up, come back, see "No password", and reasonably conclude it was
  // never saved.
  useEffect(() => {
    let cancelled = false;
    if (!dbInfoId) { setProtect({ source: "None", normalizePunctuation: true }); return undefined; }
    getDeliveryVerification(token, dbInfoId)
      .then((v) => {
        if (cancelled || !v?.configured) return;
        setProtect({
          source: v.source, fieldName: v.fieldName, template: v.template, prompt: v.prompt,
          matchMode: v.matchMode, normalizePunctuation: v.normalizePunctuation !== false,
        });
      })
      .catch(() => { /* a base with no question is the normal case, not an error */ });
    return () => { cancelled = true; };
  }, [dbInfoId, token]);

  useEffect(() => {
    let cancelled = false;
    setReadiness(null);
    if (!dbInfoId) return undefined;
    setChecking(true);
    getDeliveryBatchReadiness(token, dbInfoId)
      .then((r) => { if (!cancelled) setReadiness(r); })
      .catch(() => { if (!cancelled) setReadiness(null); })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [dbInfoId, token]);

  const customers = form.keys.split("\n").map((l) => l.trim()).filter((l) => l && !l.startsWith("#"));
  const slow = !!readiness && readiness.ready === false;

  const makeFast = async (column) => {
    setFixing(true);
    try {
      await createColumnIndex(token, dbInfoId, column);
      onNotice(t("delivery.bulkSpeedQueued", {
        defaultValue: "Speeding up this source has started. It runs in the background and does not interrupt anything; check back in a few minutes.",
      }));
      setReadiness(await getDeliveryBatchReadiness(token, dbInfoId));
    } catch (e) { onError(e); } finally { setFixing(false); }
  };

  const create = async (acknowledgeUnindexed) => {
    setRunning(true);
    setResult(null);
    try {
      const text = await generateDeliveryBatchLinks(token, {
        dbInfoId,
        period: form.period.trim() || null,
        label: form.label.trim() || null,
        formats: form.formats.trim() || null,
        keysCsv: form.keys,
        format: "ndjson",
        acknowledgeUnindexed: !!acknowledgeUnindexed,
        // The archive-backed questions live on the source and are saved separately; only the two SUPPLIED
        // kinds travel with the run, because only they are per-generation facts.
        verificationSource: protect.source,
        verificationValue: protect.source === "RunValue" ? (protect.runValue || null) : null,
        verificationPrompt: protect.prompt || null,
        verificationNormalize: protect.normalizePunctuation !== false,
      });
      const parsed = parseBatchNdjson(text);
      setResult(parsed);
      if (!parsed.complete) {
        onError(new Error(t("delivery.bulkIncomplete", {
          defaultValue: "Something went wrong part-way through. The links below are real and will work, but the run stopped early — create it again before sending anything out.",
        })));
      }
      onCreated?.();
    } catch (e) { onError(e); } finally { setRunning(false); }
  };

  const download = (kind) => {
    const rows = result?.rows || [];
    const body = kind === "csv"
      ? "recipientKey,period,token,link\n" + rows.map((r) =>
          [r.key, form.period.trim(), r.token, r.link || ""].map(csvField).join(",")).join("\n") + "\n"
      : rows.map((r) => JSON.stringify(r)).join("\n") + "\n";
    const blob = new Blob([body], { type: kind === "csv" ? "text/csv;charset=utf-8" : "application/x-ndjson;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `links-${form.period.trim() || "run"}.${kind}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {/* Step 1 */}
      <div className="asf-card">
        <Step n="1" title={t("delivery.bulkStep1", { defaultValue: "Choose the documents" })} />

        {/* Only sources whose customer-number column has been mapped can be delivered from, so with none
            mapped this list is empty. Saying that plainly beats an inert picker: the operator has no way
            to guess that the list is filled in on a different tab. */}
        {databases.length === 0 && (
          <div style={{ marginBottom: 14, padding: "12px 14px", borderRadius: 10,
                        background: "#fffbeb", border: "1px solid #fde68a" }}>
            <div style={{ fontWeight: 700, color: "#92400e", marginBottom: 6 }}>
              {t("delivery.bulkNoSourcesTitle", { defaultValue: "No document sources are ready yet" })}
            </div>
            <p style={{ fontSize: 13, color: "#78350f", margin: 0 }}>
              {t("delivery.bulkNoSourcesBody", {
                defaultValue:
                  "Before links can be created, a document source needs to know which column holds the " +
                  "customer number. Set that up on the Sources tab, then come back here.",
              })}
            </p>
          </div>
        )}

        {/* The kit's grid bottom-aligns by default, which is right when every field is just a label and
            an input. Two of these four carry a hint underneath, and bottom-alignment then lifts them
            above their neighbours — labels and inputs land on four different lines. Top-align instead,
            so the labels form one row and the hints hang below without moving anything. */}
        <div className="asf-grid" style={{ alignItems: "flex-start" }}>
          <div className="asf-field" style={{ flex: "1 1 240px" }}>
            <label className="asf-lbl">{t("delivery.bulkWhich", { defaultValue: "Which documents" })}</label>
            <Dropdown value={form.dbInfoId}
              onChange={(v) => setForm({ ...form, dbInfoId: v })}
              placeholder={t("common.select", { defaultValue: "Select…" })}
              emptyLabel={t("delivery.bulkNoSources", { defaultValue: "No sources set up" })}
              options={databases.map((d) => ({ value: String(dbId(d)), label: dbNameOf(d) }))} />
          </div>
          <div className="asf-field" style={{ flex: "0 1 210px" }}>
            <label className="asf-lbl">{t("delivery.bulkMonth", { defaultValue: "Statement month" })}</label>
            <input className="asf-input" value={form.period} placeholder="2026-07"
              onChange={(e) => setForm({ ...form, period: e.target.value })} />
            <span style={{ fontSize: 12, color: "#64748b", marginTop: 4, display: "block" }}>
              {t("delivery.bulkMonthHint", { defaultValue: "Leave empty to show the customer everything of theirs." })}
            </span>
          </div>
          <div className="asf-field" style={{ flex: "1 1 220px" }}>
            <label className="asf-lbl">{t("delivery.bulkRunName", { defaultValue: "Name for this run" })}</label>
            <input className="asf-input" value={form.label}
              placeholder={t("delivery.batchLabelPh", { defaultValue: "July 2026 statements" })}
              onChange={(e) => setForm({ ...form, label: e.target.value })} />
            <span style={{ fontSize: 12, color: "#64748b", marginTop: 4, display: "block" }}>
              {t("delivery.bulkRunNameHint", { defaultValue: "For your records only — customers never see it." })}
            </span>
          </div>
          <div className="asf-field" style={{ flex: "0 1 150px" }}>
            <label className="asf-lbl">{t("delivery.bulkFileType", { defaultValue: "File type" })}</label>
            <Dropdown value={form.formats}
              onChange={(v) => setForm({ ...form, formats: v })}
              options={[{ value: "PDF", label: "PDF" }, { value: "TIFF", label: "TIFF" }]} />
          </div>
        </div>

        {dbInfoId > 0 && (
          <SpeedNotice t={t} readiness={readiness} checking={checking} fixing={fixing} onFix={makeFast} />
        )}
      </div>

      {/* Step 2 */}
      <div className="asf-card">
        <Step n="2" title={t("delivery.bulkStep2", { defaultValue: "Add your customers" })} />
        <div className="asf-field">
          <label className="asf-lbl">{t("delivery.bulkCustomers", { defaultValue: "Customer account numbers" })}</label>
          <textarea className="asf-input" rows={7} value={form.keys}
            onChange={(e) => setForm({ ...form, keys: e.target.value })}
            placeholder={"1234567\n1234568\n1234569"} />
          <span style={{ fontSize: 12, color: "#64748b", marginTop: 4, display: "block" }}>
            {protect.source === "PerCustomer"
              ? t("delivery.bulkCustomersHintPw", {
                  defaultValue:
                    "One per line, as customer number then password separated by a comma — 1234567,04061982. " +
                    "Blank lines and lines starting with # are skipped.",
                })
              : t("delivery.bulkCustomersHint", {
                  defaultValue:
                    "One per line. You can paste your print control file straight in — the first column is used and " +
                    "anything after a comma is ignored. Blank lines and lines starting with # are skipped.",
                })}
          </span>
        </div>
        <div style={{ fontSize: 13, color: customers.length ? "#0f172a" : "#94a3b8", fontWeight: customers.length ? 700 : 400 }}>
          {customers.length === 0
            ? t("delivery.bulkNoCustomers", { defaultValue: "No customers added yet" })
            : t("delivery.bulkCustomerCount", { count: customers.length, defaultValue: "{{count}} customers" })}
        </div>
      </div>

      {/* Step 3 — the password, optional */}
      <BulkProtect t={t} busy={busy} dbInfoId={dbInfoId} columns={columns} token={token}
        value={protect} onChange={setProtect} onError={onError} onNotice={onNotice} />

      {/* Step 4 */}
      <div className="asf-card">
        <Step n="4" title={t("delivery.bulkStep4", { defaultValue: "Create the links" })} />

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <button className="asf-cta" disabled={busy || running || !dbInfoId || customers.length === 0 || slow}
            onClick={() => create(false)}>
            {running
              ? t("delivery.bulkCreating", { defaultValue: "Creating…" })
              : t("delivery.bulkCreate", { defaultValue: "Create links" })}
          </button>

          {/* Only offered once the operator has been shown WHY it is a bad idea, and styled as the
              lesser choice rather than as a second primary button. */}
          {slow && (
            <button className="asf-btn" style={{ color: "#b91c1c", borderColor: "#fecaca" }}
              disabled={busy || running || !dbInfoId || customers.length === 0}
              onClick={() => create(true)}>
              {t("delivery.bulkCreateSlow", { defaultValue: "Create anyway, accepting a slow portal" })}
            </button>
          )}

          {!dbInfoId && (
            <span style={{ fontSize: 12.5, color: "#94a3b8" }}>
              {databases.length === 0
                ? t("delivery.bulkNeedMapping", { defaultValue: "Set a source up on the Sources tab first." })
                : t("delivery.bulkNeedSource", { defaultValue: "Choose the documents in step 1 first." })}
            </span>
          )}
          {dbInfoId > 0 && customers.length === 0 && (
            <span style={{ fontSize: 12.5, color: "#94a3b8" }}>
              {t("delivery.bulkNeedCustomers", { defaultValue: "Add at least one customer in step 2." })}
            </span>
          )}
        </div>

        {result && (
          <div style={{ marginTop: 16, padding: "14px 16px", borderRadius: 10,
                        background: result.complete ? "#f0fdf4" : "#fffbeb",
                        border: `1px solid ${result.complete ? "#bbf7d0" : "#fde68a"}` }}>
            <div style={{ fontWeight: 700, color: result.complete ? "#166534" : "#92400e", marginBottom: 6 }}>
              {result.complete
                ? t("delivery.bulkReady", { count: result.rows.length, defaultValue: "{{count}} links ready" })
                : t("delivery.bulkStoppedEarly", { count: result.rows.length, defaultValue: "Stopped early — only {{count}} links were made" })}
            </div>
            <p style={{ fontSize: 13, color: "#475569", marginBottom: 12 }}>
              {t("delivery.bulkDownloadNow", {
                defaultValue:
                  "Download them now — they are not saved anywhere. If you need them again later, create the " +
                  "same run with the same details and you will get exactly the same links.",
              })}
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="asf-cta" onClick={() => download("csv")}>
                {t("delivery.bulkDownload", { defaultValue: "Download for your print system (CSV)" })}
              </button>
              <button className="asf-btn" onClick={() => download("ndjson")}>
                {t("delivery.bulkDownloadDev", { defaultValue: "Download as NDJSON" })}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/* The index report, said as a consequence the operator cares about rather than as a database fact.
   An operator does not need to know what a leading column is; they need to know their customers may
   sit watching a spinner. */
function SpeedNotice({ t, readiness, checking, fixing, onFix }) {
  if (checking) {
    return <p style={{ fontSize: 13, color: "#64748b", marginTop: 12 }}>
      {t("delivery.bulkChecking", { defaultValue: "Checking this source…" })}
    </p>;
  }
  if (!readiness) return null;

  if (readiness.ready) {
    return (
      <p style={{ fontSize: 13, color: "#166534", marginTop: 12 }}>
        ✓ {t("delivery.bulkFast", { defaultValue: "This source is set up so customers' documents open quickly." })}
      </p>
    );
  }

  // Only a plain missing index can be fixed from here. A match-mode problem needs a configuration
  // change on the Sources tab, so offering a button would be offering something that does nothing.
  const fixable = (readiness.columns || []).filter((c) => !c.indexed && c.columnExists && c.sargable);
  const modeProblem = (readiness.columns || []).some((c) => !c.sargable);

  return (
    <div style={{ marginTop: 12, padding: "12px 14px", borderRadius: 10, background: "#fffbeb", border: "1px solid #fde68a" }}>
      <div style={{ fontWeight: 700, color: "#92400e", marginBottom: 6 }}>
        {t("delivery.bulkSlowTitle", { defaultValue: "Customers would see a slow page" })}
      </div>
      <p style={{ fontSize: 13, color: "#78350f", marginBottom: fixable.length || modeProblem ? 10 : 0 }}>
        {modeProblem
          ? t("delivery.bulkSlowMode", {
              defaultValue:
                "On this source the customer number is matched in a way that cannot be sped up. Every time a " +
                "customer opens their link the whole archive is read. Ask your administrator to change the " +
                "match to “Exact” on the Sources tab.",
            })
          : t("delivery.bulkSlowIndex", {
              defaultValue:
                "This source has not been prepared for customer lookups, so every time a customer opens their " +
                "link the whole archive is read. On a large archive the portal can time out. This is a " +
                "one-time fix and it runs in the background.",
            })}
      </p>
      {fixable.map((c) => (
        <button key={c.column} className="asf-btn" disabled={fixing} onClick={() => onFix(c.column)}>
          {t("delivery.bulkSpeedUp", { defaultValue: "Speed this source up" })}
        </button>
      ))}
    </div>
  );
}

/* Step 3 — the password.

   Volkan's requirement in his words: it can be a field, a formula over fields and fixed text, a value
   fed at the call, or a column in the customer file — and all of it optional.

   Those are four different mechanisms underneath, and the operator should not have to know that. The
   screen asks one question — "how should customers prove it's them?" — and the differences show up only
   as what each choice needs from them. The two archive-backed choices carry a LIVE PREVIEW against real
   rows, because a formula typed blind is a guess: nobody can tell from {CARD:last4} that their card
   numbers are stored with dashes until customers cannot get in. */
function BulkProtect({ t, busy, dbInfoId, columns, token, value, onChange, onError, onNotice }) {
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  const source = value.source || "None";
  const archive = source === "Field" || source === "Formula";
  const expr = source === "Field"
    ? (value.fieldName ? `{${value.fieldName}}` : "")
    : (value.template || "");

  // Preview follows what the operator is typing, a beat behind, so the formula is never a guess.
  useEffect(() => {
    if (!dbInfoId || !archive || !expr) { setPreview(null); return undefined; }
    let cancelled = false;
    setPreviewing(true);
    const timer = setTimeout(() => {
      previewDeliveryVerification(token, {
        dbInfoId, template: expr,
        normalizePunctuation: value.normalizePunctuation !== false,
        matchMode: value.matchMode || "EqualsIgnoreCase",
      })
        .then((r) => { if (!cancelled) setPreview(r); })
        .catch(() => { if (!cancelled) setPreview(null); })
        .finally(() => { if (!cancelled) setPreviewing(false); });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); setPreviewing(false); };
  }, [dbInfoId, archive, expr, value.normalizePunctuation, value.matchMode, token]);

  const saveToBase = async () => {
    setSaving(true);
    try {
      await saveDeliveryVerification(token, {
        dbInfoId,
        source,
        fieldName: source === "Field" ? value.fieldName : null,
        template: source === "Formula" ? value.template : null,
        prompt: value.prompt || null,
        matchMode: value.matchMode || "EqualsIgnoreCase",
        normalizePunctuation: value.normalizePunctuation !== false,
      });
      onNotice(t("delivery.protectSaved", {
        defaultValue: "Saved. Every link created from this source will ask for it, including ones you made earlier.",
      }));
    } catch (e) { onError(e); } finally { setSaving(false); }
  };

  const choices = [
    { value: "None", label: t("delivery.protectNone", { defaultValue: "No password" }),
      hint: t("delivery.protectNoneHint", { defaultValue: "Anyone holding the link can open it." }) },
    { value: "Field", label: t("delivery.protectField", { defaultValue: "Something you already hold about them" }),
      hint: t("delivery.protectFieldHint", { defaultValue: "A date of birth, an ID number, the last digits of a card — taken straight from the document." }) },
    { value: "Formula", label: t("delivery.protectFormula", { defaultValue: "Several of their details combined" }),
      hint: t("delivery.protectFormulaHint", { defaultValue: "Build the answer from more than one detail, with fixed text if you want." }) },
    { value: "RunValue", label: t("delivery.protectRun", { defaultValue: "One password for this whole run" }),
      hint: t("delivery.protectRunHint", { defaultValue: "The same word for every customer in this batch. Simple, but one leak exposes the cycle." }) },
    { value: "PerCustomer", label: t("delivery.protectPer", { defaultValue: "A different password for each customer" }),
      hint: t("delivery.protectPerHint", { defaultValue: "Taken from a second column in the list you paste in step 2." }) },
  ];

  return (
    <div className="asf-card">
      <Step n="3" title={t("delivery.bulkStep3Protect", { defaultValue: "Protect the links" })}
        optional={t("delivery.optional", { defaultValue: "optional" })} />

      <div style={{ display: "grid", gap: 8, marginBottom: 16 }}>
        {choices.map((c) => (
          <label key={c.value}
            style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", cursor: "pointer",
                     borderRadius: 10, border: `1px solid ${source === c.value ? "#c4b5fd" : "#eef0f5"}`,
                     background: source === c.value ? "#faf5ff" : "#fff" }}>
            <input type="radio" name="bulkProtect" checked={source === c.value} style={{ marginTop: 3 }}
              onChange={() => onChange({ ...value, source: c.value })} />
            <span>
              <span style={{ fontWeight: 600, color: "#0f172a", display: "block" }}>{c.label}</span>
              <span style={{ fontSize: 12.5, color: "#64748b" }}>{c.hint}</span>
            </span>
          </label>
        ))}
      </div>

      {/* Both archive-backed choices need the source's field list, and until step 1 is answered there
          isn't one. Saying so is the difference between "you have more to do" and the flatly wrong
          "this base has no index fields" an empty picker would otherwise show. */}
      {archive && !dbInfoId && (
        <div style={{ padding: "12px 14px", borderRadius: 10, background: "#f8fafc", border: "1px solid #eef0f5",
                      fontSize: 13, color: "#475569" }}>
          {t("delivery.protectPickSourceFirst", {
            defaultValue: "Choose the source in step 1 first — the details it holds about your customers appear here.",
          })}
        </div>
      )}

      {source === "Field" && !!dbInfoId && (
        <div className="asf-grid" style={{ alignItems: "flex-start" }}>
          <div className="asf-field" style={{ flex: "1 1 260px" }}>
            <label className="asf-lbl">{t("delivery.protectWhichDetail", { defaultValue: "Which detail" })}</label>
            <Dropdown value={value.fieldName || ""}
              onChange={(v) => onChange({ ...value, fieldName: v })}
              placeholder={t("common.select", { defaultValue: "Select…" })}
              emptyLabel={t("delivery.noColumns", { defaultValue: "This base has no index fields" })}
              options={columns.map((c) => ({ value: c.name, label: c.display && c.display !== c.name ? `${c.display} (${c.name})` : c.name }))} />
          </div>
        </div>
      )}

      {source === "Formula" && !!dbInfoId && (
        <FormulaBuilder t={t} columns={columns} value={value.template || ""}
          onChange={(v) => onChange({ ...value, template: v })} />
      )}

      {source === "RunValue" && (
        <div className="asf-grid" style={{ alignItems: "flex-start" }}>
          <div className="asf-field" style={{ flex: "1 1 260px" }}>
            <label className="asf-lbl">{t("delivery.protectRunPassword", { defaultValue: "Password for this run" })}</label>
            <input className="asf-input" value={value.runValue || ""}
              onChange={(e) => onChange({ ...value, runValue: e.target.value })}
              placeholder={t("delivery.protectRunPh", { defaultValue: "e.g. the branch code you told customers" })} />
            <span style={{ fontSize: 12, color: "#64748b", marginTop: 4, display: "block" }}>
              {t("delivery.protectRunNote", {
                defaultValue:
                  "It is never stored and never appears in the links — each link carries only a check value " +
                  "derived from it. Write it down somewhere safe; it cannot be read back afterwards.",
              })}
            </span>
          </div>
        </div>
      )}

      {source === "PerCustomer" && (
        <div style={{ padding: "12px 14px", borderRadius: 10, background: "#f8fafc", border: "1px solid #eef0f5" }}>
          <p style={{ fontSize: 13, color: "#475569", margin: "0 0 8px" }}>
            {t("delivery.protectPerBody", {
              defaultValue:
                "Put the password in the second column of the list in step 2, after the customer number:",
            })}
          </p>
          <pre style={{ margin: 0, fontSize: 12.5, background: "#0f172a", color: "#e6edf3", padding: "10px 12px", borderRadius: 8 }}>
{`1234567,04061982
1234568,17091975`}
          </pre>
          <span style={{ fontSize: 12, color: "#64748b", marginTop: 8, display: "block" }}>
            {t("delivery.protectPerNote", {
              defaultValue: "Rows without a password are refused rather than created unprotected, so nobody is silently left open.",
            })}
          </span>
        </div>
      )}

      {source !== "None" && (
        <>
          <div className="asf-grid" style={{ alignItems: "flex-start", marginTop: 14 }}>
            <div className="asf-field" style={{ flex: "1 1 320px" }}>
              <label className="asf-lbl">{t("delivery.protectPrompt", { defaultValue: "What the customer is asked" })}</label>
              <input className="asf-input" value={value.prompt || ""}
                onChange={(e) => onChange({ ...value, prompt: e.target.value })}
                placeholder={t("delivery.protectPromptPh", { defaultValue: "Your date of birth (DDMMYYYY)" })} />
              <span style={{ fontSize: 12, color: "#64748b", marginTop: 4, display: "block" }}>
                {t("delivery.protectPromptHint", {
                  defaultValue: "This is the only clue they get, so say the format you expect.",
                })}
              </span>
            </div>
            <div className="asf-field" style={{ flex: "0 1 260px" }}>
              <label className="asf-lbl">{t("delivery.protectTyping", { defaultValue: "Typing" })}</label>
              <ToggleSwitch checked={value.normalizePunctuation !== false}
                onChange={(v) => onChange({ ...value, normalizePunctuation: v })}
                label={t("delivery.protectForgiving", { defaultValue: "Ignore spaces, dashes and capitals" })} />
              <span style={{ fontSize: 12, color: "#64748b", marginTop: 4, display: "block" }}>
                {t("delivery.protectForgivingHint", {
                  defaultValue: "Recommended. 04/06/1982 and 04-06-1982 are then both accepted; the answer itself still has to be right.",
                })}
              </span>
            </div>
          </div>

          {archive && (
            <PreviewPanel t={t} preview={preview} previewing={previewing} hasExpr={!!expr} />
          )}

          {archive && (
            <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <button className="asf-cta" disabled={busy || saving || !dbInfoId || !expr || (preview && preview.ok === false)}
                onClick={saveToBase}>
                {saving
                  ? t("common.saving", { defaultValue: "Saving…" })
                  : t("delivery.protectSave", { defaultValue: "Save this question for this source" })}
              </button>
              <span style={{ fontSize: 12, color: "#64748b" }}>
                {t("delivery.protectSaveHint", {
                  defaultValue: "It belongs to the source, so it also applies to links you created earlier.",
                })}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── The answer editor ─────────────────────────────────────────────────────────────────────────────
   This is the Index Mapping expression editor, in the same visual language and on the same CSS: source
   fields as chips, functions as chips, and an expression built by clicking rather than typed. Anyone who
   has mapped a column on the Database Properties screen already knows how to use it.

   It reuses that screen's look, not its grammar. A mapping expression is a nesting of functions over
   fields; a verification answer is a field with a chain of format steps — {KART_NO:digits|last4}. Those
   are the same idea written differently, so the pills read exactly as they do there, ƒ LAST 4( ƒ DIGITS(
   KART_NO ) ), while what gets saved is still the chain the server already knows how to evaluate. No
   engine change, and the 34 tests behind DeliveryVerification stay meaningful.

   Reading a stored template back matters as much as writing one: a question saved before this editor
   existed, or edited by hand in the database, must open here rather than being silently discarded. */

/** Splits a stored template into editable parts. An unclosed brace is kept as literal text rather than
    dropped, so nothing an operator cannot see can survive a round trip through this editor. */
export function parseTemplate(tpl) {
  const parts = [];
  let i = 0;
  while (i < tpl.length) {
    const open = tpl.indexOf("{", i);
    if (open < 0) { parts.push({ kind: "text", text: tpl.slice(i) }); break; }
    if (open > i) parts.push({ kind: "text", text: tpl.slice(i, open) });
    const close = tpl.indexOf("}", open + 1);
    if (close < 0) { parts.push({ kind: "text", text: tpl.slice(open) }); break; }
    const body = tpl.slice(open + 1, close);
    const colon = body.indexOf(":");
    // Only the FIRST colon separates the name from the format — HH:mm is one pattern, not two.
    const name = (colon < 0 ? body : body.slice(0, colon)).trim();
    const fmt = colon < 0 ? "" : body.slice(colon + 1).trim();
    parts.push({ kind: "field", name, steps: fmt ? fmt.split("|").map((s) => s.trim()).filter(Boolean) : [] });
    i = close + 1;
  }
  return parts.filter((p) => p.kind !== "text" || p.text.length > 0);
}

export function buildTemplate(parts) {
  return parts
    .map((p) => (p.kind === "field"
      ? (p.steps.length ? `{${p.name}:${p.steps.join("|")}}` : `{${p.name}}`)
      : p.text))
    .join("");
}

const ANSWER_DATE_FORMATS = ["ddMMyyyy", "dd/MM/yyyy", "dd.MM.yyyy", "yyyyMMdd", "yyyy-MM-dd", "MM/dd/yyyy", "MMyyyy", "yyyy"];

/* The function chips, in the shape the mapping editor uses: name, short icon, one-line description, and
   whatever parameters the step needs. Each returns the format step it appends to the chosen detail. */
function answerFuncs(t) {
  return [
    { name: "DIGITS", label: t("delivery.fnDigits", { defaultValue: "Digits only" }), icon: "123",
      desc: t("delivery.fnDigitsD", { defaultValue: "Drop dashes, spaces and letters — keep the digits" }),
      params: [], step: () => "digits" },
    { name: "LAST", label: t("delivery.fnLast", { defaultValue: "Last N" }), icon: "[ ]",
      desc: t("delivery.fnLastD", { defaultValue: "Keep only the last few characters" }),
      params: [{ key: "n", label: t("delivery.fnHowMany", { defaultValue: "How many" }), type: "number", def: "4" }],
      step: (p) => `last${p.n || "4"}` },
    { name: "FIRST", label: t("delivery.fnFirst", { defaultValue: "First N" }), icon: "[ ]",
      desc: t("delivery.fnFirstD", { defaultValue: "Keep only the first few characters" }),
      params: [{ key: "n", label: t("delivery.fnHowMany", { defaultValue: "How many" }), type: "number", def: "4" }],
      step: (p) => `first${p.n || "4"}` },
    { name: "DATEFORMAT", label: t("delivery.fnDate", { defaultValue: "Date Format" }), icon: "📅",
      desc: t("delivery.fnDateD", { defaultValue: "Write a date the way the customer would type it" }),
      params: [{ key: "format", label: t("delivery.fnFormat", { defaultValue: "Format" }), type: "text", def: "ddMMyyyy", list: ANSWER_DATE_FORMATS }],
      step: (p) => p.format || "ddMMyyyy" },
    { name: "UPPER", label: t("delivery.fnUpper", { defaultValue: "Uppercase" }), icon: "Aa↑",
      desc: t("delivery.fnUpperD", { defaultValue: "Rarely needed — capitals are already ignored when comparing" }),
      params: [], step: () => "upper" },
    { name: "LOWER", label: t("delivery.fnLower", { defaultValue: "Lowercase" }), icon: "aa↓",
      desc: t("delivery.fnLowerD", { defaultValue: "Rarely needed, for the same reason" }),
      params: [], step: () => "lower" },
  ];
}

/** How one format step is written on its pill. A date pattern describes itself; the named steps do not. */
function stepLabel(step) {
  const last = /^last(\d+)$/i.exec(step);
  if (last) return `LAST ${last[1]}`;
  const first = /^first(\d+)$/i.exec(step);
  if (first) return `FIRST ${first[1]}`;
  if (/^(digits|upper|lower)$/i.test(step)) return step.toUpperCase();
  return step;
}

export function FormulaBuilder({ t, columns, value, onChange }) {
  const parts = useMemo(() => parseTemplate(value || ""), [value]);
  const funcs = useMemo(() => answerFuncs(t), [t]);

  const [focus, setFocus] = useState(null);     // index of the detail a function would apply to
  const [panel, setPanel] = useState(null);     // { fn, params } while a parameterised chip is open
  const appendRef = useRef(null);

  const commit = (next) => onChange(buildTemplate(next));

  // Functions act on one detail. The last one added is nearly always the one meant, but an operator
  // fixing a formula halfway along should not have to rebuild the tail, so clicking a token retargets.
  const lastFieldIdx = (() => {
    for (let i = parts.length - 1; i >= 0; i--) if (parts[i].kind === "field") return i;
    return -1;
  })();
  const targetIdx = (focus != null && parts[focus]?.kind === "field") ? focus : lastFieldIdx;
  const target = targetIdx >= 0 ? parts[targetIdx] : null;

  // Whatever is half-typed in the trailing box is fixed text the operator meant to keep; flush it before
  // anything else lands, rather than discarding it because they reached for a chip next.
  const flushPending = (cur) => {
    const pending = appendRef.current?.value || "";
    if (!pending) return cur;
    appendRef.current.value = "";
    return [...cur, { kind: "text", text: pending.replace(/[{}]/g, "") }];
  };

  const addField = (name) => {
    const next = [...flushPending(parts), { kind: "field", name, steps: [] }];
    setFocus(next.length - 1);
    commit(next);
    setTimeout(() => appendRef.current?.focus(), 0);
  };

  const addStep = (fn, params) => {
    if (targetIdx < 0) return;
    const next = parts.map((p, i) => (i === targetIdx ? { ...p, steps: [...p.steps, fn.step(params)] } : p));
    setPanel(null);
    commit(next);
  };

  const clickFunc = (fn) => {
    if (targetIdx < 0) return;                                   // nothing to apply to; the hint says so
    if (!fn.params.length) { addStep(fn, {}); return; }
    const defaults = {};
    fn.params.forEach((p) => { defaults[p.key] = p.def; });
    setPanel((prev) => (prev?.fn.name === fn.name ? null : { fn, params: defaults }));
  };

  const removeStep = (partIdx, stepIdx) =>
    commit(parts.map((p, i) => (i === partIdx ? { ...p, steps: p.steps.filter((_, k) => k !== stepIdx) } : p)));

  const removePart = (i) => { setFocus(null); commit(parts.filter((_, k) => k !== i)); };

  // Braces are stripped from fixed text so a stray one cannot turn literal text into a field reference
  // that silently changes the answer.
  const setText = (i, raw) => {
    const clean = raw.replace(/[{}]/g, "");
    commit(clean === "" ? parts.filter((_, k) => k !== i) : parts.map((p, k) => (k === i ? { kind: "text", text: clean } : p)));
  };

  const flushAppend = () => {
    const v = appendRef.current?.value || "";
    if (!v) return;
    appendRef.current.value = "";
    commit([...parts, { kind: "text", text: v.replace(/[{}]/g, "") }]);
  };

  return (
    <>
      {/* ── Source fields ── */}
      <div className="dbExprModalSection" style={{ marginTop: 4 }}>
        <div className="dbExprModalSectionLabel">
          {t("delivery.exprSources", { defaultValue: "Details this source holds" })}
        </div>
        {columns.length === 0 ? (
          <p style={{ fontSize: 12.5, color: "#64748b", margin: 0 }}>
            {t("delivery.noColumns", { defaultValue: "This base has no index fields" })}
          </p>
        ) : (
          <div className="dbMappingSourceChips">
            {columns.map((c) => (
              <button key={c.name} type="button" className="dbMappingSourceChip"
                onMouseDown={(e) => e.preventDefault()} onClick={() => addField(c.name)}
                title={c.display && c.display !== c.name ? c.display : `${t("delivery.exprInsert", { defaultValue: "Add" })} ${c.name}`}>
                {c.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Functions ── */}
      <div className="dbExprModalSection" style={{ marginTop: 12 }}>
        <div className="dbExprModalSectionLabel">
          {t("delivery.exprFunctions", { defaultValue: "How much of it" })}
        </div>
        <div className="dbMappingFuncChips">
          {funcs.map((fn) => (
            <button key={fn.name} type="button"
              className={`dbMappingFuncChip${panel?.fn.name === fn.name ? " active" : ""}${targetIdx < 0 ? " unfocused" : ""}`}
              onMouseDown={(e) => { e.preventDefault(); clickFunc(fn); }} title={fn.desc}>
              <span className="dbMappingFuncChipIcon">{fn.icon}</span>
              <span className="dbMappingFuncChipLabel">{fn.label}</span>
            </button>
          ))}
        </div>

        {targetIdx < 0 ? (
          <div className="dbMappingFuncHint">
            {t("delivery.exprPickFieldFirst", { defaultValue: "Add a detail first — these trim and reshape it." })}
          </div>
        ) : (
          <div className="dbExprModalFieldHint">
            {t("delivery.exprAppliesTo", { defaultValue: "Applies to {{name}} — click another detail below to change that.", name: target.name })}
          </div>
        )}

        {panel && (
          <div className="dbMappingFuncParamPanel">
            <div className="dbMappingFuncParamHeader">
              <span className="dbMappingFuncParamFnName">ƒ {panel.fn.label}</span>
              <span className="dbMappingFuncParamDivider">—</span>
              <span className="dbMappingFuncParamDesc">{panel.fn.desc}</span>
              <span className="dbMappingFuncParamPreview">{stepLabel(panel.fn.step(panel.params))}</span>
            </div>
            <div className="dbMappingFuncParamBody">
              {panel.fn.params.map((pd) => (
                <label key={pd.key} className="dbMappingFuncParamField">
                  <span className="dbMappingFuncParamLabel">{pd.label}</span>
                  <input type={pd.type} className="dbMappingFuncParamInput"
                    value={panel.params[pd.key] ?? pd.def}
                    list={pd.list ? `deliveryAnswerFmt_${pd.key}` : undefined}
                    onChange={(e) => setPanel((prev) => ({ ...prev, params: { ...prev.params, [pd.key]: e.target.value } }))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addStep(panel.fn, panel.params); } }} />
                  {pd.list && (
                    <datalist id={`deliveryAnswerFmt_${pd.key}`}>
                      {pd.list.map((f) => <option key={f} value={f} />)}
                    </datalist>
                  )}
                </label>
              ))}
              <div className="dbMappingFuncParamActions">
                <button type="button" className="dbMappingFuncParamApply"
                  onMouseDown={(e) => { e.preventDefault(); addStep(panel.fn, panel.params); }}>
                  {t("delivery.exprApply", { defaultValue: "Insert" })}
                </button>
                <button type="button" className="dbMappingFuncParamCancel"
                  onMouseDown={(e) => { e.preventDefault(); setPanel(null); }}>
                  {t("common.cancel", { defaultValue: "Cancel" })}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── The answer ── */}
      <div className="dbExprModalSection dbExprModalBuilderSection" style={{ marginTop: 12 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <div className="dbExprModalSectionLabel" style={{ flex: 1 }}>
            {t("delivery.exprAnswer", { defaultValue: "What the customer types" })}
          </div>
          {parts.length > 0 && (
            <button type="button" className="dbExprModalClearBtn"
              style={{ height: 22, padding: "0 8px", fontSize: 11 }}
              onClick={() => { setFocus(null); setPanel(null); commit([]); }}>
              {t("common.clear", { defaultValue: "Clear" })}
            </button>
          )}
        </div>

        <div className="dbExprBuilder" onClick={() => appendRef.current?.focus()}>
          {parts.map((part, i) => {
            if (part.kind === "text") {
              return (
                <input key={`t${i}`} type="text" className="dbExprLiteralInput"
                  aria-label={t("delivery.exprFixed", { defaultValue: "Fixed text" })}
                  value={part.text} size={Math.max(1, part.text.length)} spellCheck={false}
                  onChange={(e) => setText(i, e.target.value)} />
              );
            }
            // Steps read outermost-first, exactly as the mapping editor writes nested calls: the last
            // step applied is the one wrapping everything else.
            const open = part.steps.map((s, k) => ({ s, k })).reverse();
            return (
              <React.Fragment key={`f${i}`}>
                {open.map(({ s, k }) => (
                  <span key={`o${k}`} className="dbExprFuncToken">
                    <span className="dbExprFuncIcon">ƒ</span>
                    <span className="dbExprFuncName">{stepLabel(s)}</span>
                    <span className="dbExprFuncParen">(</span>
                    <button type="button" className="dbExprTokenX" tabIndex={-1}
                      title={`${t("common.remove", { defaultValue: "Remove" })} ${stepLabel(s)}`}
                      onMouseDown={(e) => { e.preventDefault(); removeStep(i, k); }}>×</button>
                  </span>
                ))}
                <span className={`dbExprToken${targetIdx === i ? " selected" : ""}`}
                  style={targetIdx === i ? { outline: "2px solid #c4b5fd", outlineOffset: 1 } : undefined}>
                  <span className="dbExprTokenLabel" style={{ cursor: "pointer" }}
                    title={t("delivery.exprRetarget", { defaultValue: "Apply the next function to this detail" })}
                    onMouseDown={(e) => { e.preventDefault(); setFocus(i); }}>{part.name}</span>
                  <button type="button" className="dbExprTokenX" tabIndex={-1}
                    title={t("common.remove", { defaultValue: "Remove" })}
                    onMouseDown={(e) => { e.preventDefault(); removePart(i); }}>×</button>
                </span>
                {part.steps.map((_, k) => <span key={`c${k}`} className="dbExprFuncClose">)</span>)}
              </React.Fragment>
            );
          })}
          <input ref={appendRef} type="text" className="dbExprAppendInput" spellCheck={false}
            placeholder={parts.length === 0
              ? t("delivery.exprPlaceholder", { defaultValue: "click a detail above, or type fixed text…" })
              : ""}
            onBlur={flushAppend}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); flushAppend(); } }} />
        </div>

        {parts.length > 0 && (
          <div className="dbExprModalPreview">
            <span className="dbExprModalPreviewLabel">{t("delivery.exprSaved", { defaultValue: "Saved as" })}</span>
            <code className="dbExprModalPreviewCode">{buildTemplate(parts)}</code>
          </div>
        )}

        <span style={{ fontSize: 12, color: "#64748b", display: "block" }}>
          {t("delivery.formulaHint2", {
            defaultValue:
              "The customer types the parts in this order, including any fixed text. Check the preview below " +
              "before saving — it shows what real customers of this source would have to type.",
          })}
        </span>
      </div>
    </>
  );
}

/* Setting a source's question from the Sources tab.

   Only the two ARCHIVE-BACKED sources appear here. A run password ("one password for this batch",
   "a different one per customer") is a fact about a particular generation and cannot be attached to a
   base after the fact — it lives in step 3 of the run wizard, and offering it here would promise
   something that could not be applied to the links already in customers' hands. */
function SourceQuestion({ t, token, dbInfoId, columns, current, onSaved, onError, onNotice }) {
  const [value, setValue] = useState(() => ({
    source: current?.source || "None",
    fieldName: current?.fieldName || "",
    template: current?.template || "",
    prompt: current?.prompt || "",
    matchMode: current?.matchMode || "EqualsIgnoreCase",
    normalizePunctuation: current?.normalizePunctuation !== false,
  }));
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  const source = value.source;
  const archive = source === "Field" || source === "Formula";
  const expr = source === "Field" ? (value.fieldName ? `{${value.fieldName}}` : "") : (value.template || "");

  useEffect(() => {
    if (!archive || !expr) { setPreview(null); return undefined; }
    let cancelled = false;
    setPreviewing(true);
    const timer = setTimeout(() => {
      previewDeliveryVerification(token, {
        dbInfoId, template: expr,
        normalizePunctuation: value.normalizePunctuation !== false,
        matchMode: value.matchMode || "EqualsIgnoreCase",
      })
        .then((r) => { if (!cancelled) setPreview(r); })
        .catch(() => { if (!cancelled) setPreview(null); })
        .finally(() => { if (!cancelled) setPreviewing(false); });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); setPreviewing(false); };
  }, [dbInfoId, archive, expr, value.normalizePunctuation, value.matchMode, token]);

  const save = async () => {
    setSaving(true);
    try {
      await saveDeliveryVerification(token, {
        dbInfoId,
        source,
        fieldName: source === "Field" ? value.fieldName : null,
        template: source === "Formula" ? value.template : null,
        prompt: value.prompt || null,
        matchMode: value.matchMode || "EqualsIgnoreCase",
        normalizePunctuation: value.normalizePunctuation !== false,
      });
      onNotice(source === "None"
        ? t("delivery.questionCleared", {
            defaultValue: "Removed. Links from this source open without a question again — including ones already sent.",
          })
        : t("delivery.questionSaved", {
            defaultValue: "Saved. Every link from this source now asks it, including ones you sent months ago.",
          }));
      onSaved();
    } catch (e) { onError(e); } finally { setSaving(false); }
  };

  const choices = [
    { value: "None", label: t("delivery.protectNone", { defaultValue: "No password" }),
      hint: t("delivery.protectNoneHint", { defaultValue: "Anyone holding the link can open it." }) },
    { value: "Field", label: t("delivery.protectField", { defaultValue: "Something you already hold about them" }),
      hint: t("delivery.protectFieldHint", { defaultValue: "A date of birth, an ID number, the last digits of a card — taken straight from the document." }) },
    { value: "Formula", label: t("delivery.protectFormula", { defaultValue: "Several of their details combined" }),
      hint: t("delivery.protectFormulaHint", { defaultValue: "Build the answer from more than one detail, with fixed text if you want." }) },
  ];

  const dirty = source !== (current?.source || "None")
    || (value.prompt || "") !== (current?.prompt || "")
    || expr !== (current?.template || (current?.fieldName ? `{${current.fieldName}}` : ""))
    || (value.normalizePunctuation !== false) !== (current?.normalizePunctuation !== false);

  return (
    <div>
      <p style={{ fontSize: 13, color: "#475569", margin: "10px 0 12px" }}>
        {t("delivery.questionIntro", {
          defaultValue:
            "The question is part of the source, not of one batch, and it is checked when a customer opens " +
            "their link — so it also protects links you have already printed and posted.",
        })}
      </p>

      <div style={{ display: "grid", gap: 8, marginBottom: 14, maxWidth: 760 }}>
        {choices.map((c) => (
          <label key={c.value}
            style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", cursor: "pointer",
                     borderRadius: 10, border: `1px solid ${source === c.value ? "#c4b5fd" : "#eef0f5"}`,
                     background: source === c.value ? "#faf5ff" : "#fff" }}>
            <input type="radio" name={`srcQ${dbInfoId}`} checked={source === c.value} style={{ marginTop: 3 }}
              onChange={() => setValue({ ...value, source: c.value })} />
            <span>
              <span style={{ fontWeight: 600, color: "#0f172a", display: "block" }}>{c.label}</span>
              <span style={{ fontSize: 12.5, color: "#64748b" }}>{c.hint}</span>
            </span>
          </label>
        ))}
      </div>

      {source === "Field" && (
        <div className="asf-field" style={{ maxWidth: 340 }}>
          <label className="asf-lbl">{t("delivery.protectWhichDetail", { defaultValue: "Which detail" })}</label>
          <Dropdown value={value.fieldName} onChange={(v) => setValue({ ...value, fieldName: v })}
            placeholder={t("common.select", { defaultValue: "Select…" })}
            emptyLabel={t("delivery.noColumns", { defaultValue: "This base has no index fields" })}
            options={columns.map((c) => ({ value: c.name, label: c.display && c.display !== c.name ? `${c.display} (${c.name})` : c.name }))} />
        </div>
      )}

      {source === "Formula" && (
        <FormulaBuilder t={t} columns={columns} value={value.template || ""}
          onChange={(v) => setValue({ ...value, template: v })} />
      )}

      {archive && (
        <>
          <div className="asf-grid" style={{ alignItems: "flex-start", marginTop: 14 }}>
            <div className="asf-field" style={{ flex: "1 1 320px" }}>
              <label className="asf-lbl">{t("delivery.protectPrompt", { defaultValue: "What the customer is asked" })}</label>
              <input className="asf-input" value={value.prompt || ""}
                onChange={(e) => setValue({ ...value, prompt: e.target.value })}
                placeholder={t("delivery.protectPromptPh", { defaultValue: "Your date of birth (DDMMYYYY)" })} />
              <span style={{ fontSize: 12, color: "#64748b", marginTop: 4, display: "block" }}>
                {t("delivery.protectPromptHint", { defaultValue: "This is the only clue they get, so say the format you expect." })}
              </span>
            </div>
            <div className="asf-field" style={{ flex: "0 1 260px" }}>
              <label className="asf-lbl">{t("delivery.protectTyping", { defaultValue: "Typing" })}</label>
              <ToggleSwitch checked={value.normalizePunctuation !== false}
                onChange={(v) => setValue({ ...value, normalizePunctuation: v })}
                label={t("delivery.protectForgiving", { defaultValue: "Ignore spaces, dashes and capitals" })} />
            </div>
          </div>
          <PreviewPanel t={t} preview={preview} previewing={previewing} hasExpr={!!expr} />
        </>
      )}

      <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <button className="asf-cta"
          disabled={saving || !dirty || (archive && (!expr || (preview && preview.ok === false)))}
          onClick={save}>
          {saving ? t("common.saving", { defaultValue: "Saving…" })
                  : source === "None"
                    ? t("delivery.questionRemove", { defaultValue: "Remove the question" })
                    : t("delivery.questionSave", { defaultValue: "Save the question" })}
        </button>
        {source === "None" && current && (
          <span style={{ fontSize: 12.5, color: "#92400e" }}>
            {t("delivery.questionRemoveWarn", {
              defaultValue: "Every link from this source will open without asking anything, including ones already sent.",
            })}
          </span>
        )}
      </div>
    </div>
  );
}

/* What the formula produces for real customers. This is the whole reason the formula editor is usable. */
function PreviewPanel({ t, preview, previewing, hasExpr }) {
  if (!hasExpr) return null;

  if (previewing && !preview) {
    return <p style={{ fontSize: 13, color: "#64748b", marginTop: 14 }}>
      {t("delivery.previewChecking", { defaultValue: "Checking against real documents…" })}
    </p>;
  }
  if (!preview) return null;

  if (preview.ok === false) {
    return (
      <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 10, background: "#fef2f2", border: "1px solid #fecaca" }}>
        <div style={{ fontWeight: 700, color: "#991b1b", marginBottom: 4 }}>
          {t("delivery.previewBad", { defaultValue: "This will not work" })}
        </div>
        <p style={{ fontSize: 13, color: "#7f1d1d", margin: 0 }}>{preview.error}</p>
      </div>
    );
  }

  const samples = preview.samples || [];
  const anyEmpty = !!preview.anyEmpty;

  return (
    <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 10,
                  background: anyEmpty ? "#fffbeb" : "#f0fdf4",
                  border: `1px solid ${anyEmpty ? "#fde68a" : "#bbf7d0"}` }}>
      <div style={{ fontWeight: 700, color: anyEmpty ? "#92400e" : "#166534", marginBottom: 8 }}>
        {anyEmpty
          ? t("delivery.previewEmpty", { defaultValue: "Some customers would have no password at all" })
          : t("delivery.previewGood", { defaultValue: "What your customers would type" })}
      </div>

      {samples.length === 0 && (
        <p style={{ fontSize: 13, color: "#475569", margin: 0 }}>
          {t("delivery.previewNoRows", { defaultValue: "This source has no documents yet, so there is nothing to check against." })}
        </p>
      )}

      {samples.map((sm, i) => (
        <div key={i} style={{ display: "flex", gap: 12, alignItems: "baseline", fontSize: 13, marginBottom: 4 }}>
          {sm.customer && <code style={{ color: "#64748b" }}>{sm.customer}</code>}
          <b style={{ fontFamily: "ui-monospace, monospace", color: sm.empty ? "#b91c1c" : "#0f172a" }}>
            {sm.empty ? t("delivery.previewNothing", { defaultValue: "(nothing)" }) : sm.value}
          </b>
          {!sm.empty && sm.compared !== sm.value && (
            <span style={{ color: "#64748b", fontSize: 12 }}>
              {t("delivery.previewAlso", { value: sm.compared, defaultValue: "also accepted as {{value}}" })}
            </span>
          )}
        </div>
      ))}

      {anyEmpty && (
        <p style={{ fontSize: 12.5, color: "#78350f", margin: "8px 0 0" }}>
          {t("delivery.previewEmptyWhy", {
            defaultValue:
              "A customer whose detail is blank cannot be let in — an empty answer is never accepted — so they " +
              "would be locked out of their own statements. Pick a detail every customer has.",
          })}
        </p>
      )}
    </div>
  );
}

function BulkBlocked({ t, busy, blocked, onRevokeKey }) {
  const [key, setKey] = useState("");

  return (
    <div className="asf-card">
      <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>
        {t("delivery.bulkBlocked", { defaultValue: "Blocked customers" })}
      </div>
      <p style={{ fontSize: 13, color: "#475569", marginBottom: 12 }}>
        {t("delivery.bulkBlockedHint", {
          defaultValue:
            "Blocking a customer stops every link they have, straight away — including a page they already have " +
            "open. No other customer is affected. You can send them new links afterwards.",
        })}
      </p>

      <form className="asf-grid" style={{ alignItems: "flex-end" }}
        onSubmit={(e) => { e.preventDefault(); if (key.trim()) { onRevokeKey(key.trim()); setKey(""); } }}>
        <div className="asf-field" style={{ flex: "1 1 240px" }}>
          <label className="asf-lbl">{t("delivery.bulkCustomerNo", { defaultValue: "Customer account number" })}</label>
          <input className="asf-input" value={key} onChange={(e) => setKey(e.target.value)}
            placeholder={t("delivery.batchRevokeKeyPh", { defaultValue: "account or subscriber number" })} />
        </div>
        <button className="asf-btn" style={{ color: "#b91c1c", borderColor: "#fecaca" }} type="submit" disabled={busy || !key.trim()}>
          {t("delivery.bulkBlockBtn", { defaultValue: "Block their links" })}
        </button>
      </form>

      {blocked.length === 0 ? (
        <EmptyState icon="🔓" title={t("delivery.bulkNoneBlockedYet", { defaultValue: "No customers are blocked" })} />
      ) : (
        <div className="asf-tablecard">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #eef0f5" }}>
                <Th>{t("delivery.bulkCustomerNo", { defaultValue: "Customer account number" })}</Th>
                <Th>{t("delivery.bulkBlockedOn", { defaultValue: "Blocked on" })}</Th>
                <Th>{t("delivery.reason", { defaultValue: "Reason" })}</Th>
                <Th>{t("delivery.bulkBlockedBy", { defaultValue: "Blocked by" })}</Th>
              </tr>
            </thead>
            <tbody>
              {blocked.map((r) => (
                <tr key={r.id} className="asf-row" style={{ borderBottom: "1px solid #f4f5f8" }}>
                  <Td><code>{r.recipientKey}</code></Td>
                  <Td>{fmtAbs(r.updatedAt || r.createdAt)}</Td>
                  <Td>{r.reason || "—"}</Td>
                  <Td>{r.updatedBy || r.createdBy || "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BulkHistory({ t, runs }) {
  return (
    <div className="asf-card">
      <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: 12 }}>
        {t("delivery.bulkHistory", { defaultValue: "Recent runs" })}
      </div>
      {(!runs || runs.length === 0) ? (
        <EmptyState icon="📦" title={t("delivery.bulkNoRuns", { defaultValue: "You have not created any links yet" })} />
      ) : (
        <div className="asf-tablecard">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #eef0f5" }}>
                <Th>{t("delivery.bulkRunName", { defaultValue: "Name for this run" })}</Th>
                <Th>{t("delivery.bulkWhich", { defaultValue: "Which documents" })}</Th>
                <Th>{t("delivery.bulkMonth", { defaultValue: "Statement month" })}</Th>
                <Th>{t("delivery.batchCount", { defaultValue: "Links" })}</Th>
                <Th>{t("delivery.expires", { defaultValue: "Expires" })}</Th>
                <Th>{t("delivery.batchWhen", { defaultValue: "When" })}</Th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="asf-row" style={{ borderBottom: "1px solid #f4f5f8" }}>
                  <Td><b>{r.label || "—"}</b></Td>
                  <Td>{r.dbName || r.dBInfoId}</Td>
                  <Td>{r.period || "—"}</Td>
                  <Td>{Number(r.linkCount ?? 0).toLocaleString()}</Td>
                  <Td>{r.expiresAt ? fmtAbs(r.expiresAt) : t("delivery.batchNever", { defaultValue: "never" })}</Td>
                  <Td>
                    {fmtAbs(r.createdAt)}
                    {r.createdBy ? ` · ${r.createdBy}` : ""}
                    {/* An offline run left no other trace in the archive, so it has to be visible here. */}
                    {r.source === "cli" && (
                      <span className="asf-status asf-status--warn" style={{ marginLeft: 8 }}>
                        {t("delivery.bulkMadeOffline", { defaultValue: "made offline" })}
                      </span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* The standing rules, and the two actions that can destroy something. Nothing here is part of
   making this month's links, which is why it is its own pane.

   The rules and the destructive actions are separate cards rather than one list with rules
   between them: reading "how long links keep working" and clicking "stop every link" should not
   be the same gesture with a different landing spot. */
function BulkAdvanced({ t, busy, policy, onSavePolicy, onRevokeAll }) {
  const MONTH = 2628000;
  const secs = policy?.batchLinkLifetimeSeconds;
  const current = secs == null ? "0" : String(Math.round(secs / MONTH));

  const choices = ["0", "6", "12", "24", "36"];
  const options = [
    { value: "0", label: t("delivery.bulkLifeNever", { defaultValue: "Links never expire" }) },
    ...["6", "12", "24", "36"].map((m) => ({
      value: m, label: t("delivery.bulkLifeMonths", { count: Number(m), defaultValue: "Links expire after {{count}} months" }),
    })),
  ];
  // A value set through the API that is not one of the offered choices must still be visible, or
  // opening this panel would silently misreport what the tenant is actually configured for.
  if (!choices.includes(current)) {
    options.push({ value: current, label: t("delivery.bulkLifeMonths", { count: Number(current), defaultValue: "Links expire after {{count}} months" }) });
  }

  return (
    <>
      <div className="asf-card">
        <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>
          {t("delivery.bulkRules", { defaultValue: "Rules for every link" })}
        </div>
        <p style={{ fontSize: 13, color: "#475569", marginBottom: 14 }}>
          {t("delivery.bulkRulesHint", {
            defaultValue: "These apply to the whole organization, not to one run. They are saved as you change them.",
          })}
        </p>

        <div className="asf-grid" style={{ alignItems: "flex-start" }}>
          <div className="asf-field" style={{ flex: "1 1 280px" }}>
            <label className="asf-lbl">{t("delivery.bulkHowLong", { defaultValue: "How long links keep working" })}</label>
            <Dropdown value={current}
              onChange={(v) => onSavePolicy({ batchLinkLifetimeSeconds: Number(v) === 0 ? 0 : Number(v) * MONTH })}
              options={options} />
            <span style={{ fontSize: 12, color: "#64748b", marginTop: 4, display: "block" }}>
              {t("delivery.bulkHowLongHint", {
                defaultValue: "Applies to links created from now on. Links already sent out keep the setting they were made with.",
              })}
            </span>
          </div>

          {/* The fail-closed switch. It lives here rather than beside the Protect step because it is a
              standing rule for the whole organization, not a choice about one run — and because turning
              it on stops links working for sources that have no question, which is the point. */}
          <div className="asf-field" style={{ flex: "1 1 300px" }}>
            <label className="asf-lbl">{t("delivery.bulkRequireLbl", { defaultValue: "Always ask a question" })}</label>
            <ToggleSwitch checked={!!policy?.batchLinkRequireVerification}
              onChange={(v) => onSavePolicy({ batchLinkRequireVerification: v })}
              label={t("delivery.bulkRequire", { defaultValue: "Every link must be protected" })} />
            <span style={{ fontSize: 12, color: "#64748b", marginTop: 4, display: "block" }}>
              {policy?.batchLinkRequireVerification
                ? t("delivery.bulkRequireOn", {
                    defaultValue:
                      "On. Links from a source with no question set up will not open — for anyone — until " +
                      "one is saved in step 3. Check every source you create links from.",
                  })
                : t("delivery.bulkRequireOff", {
                    defaultValue:
                      "Off. A link is protected only when its source has a question, or when you chose a " +
                      "password for the run.",
                  })}
            </span>
          </div>
      </div>
      </div>

      {/* Not a styling flourish: these two are the only controls on the tab that take something away,
          and the operator gets to see that before the pointer is over the button. */}
      <div className="asf-card asf-card--danger">
        <div style={{ fontWeight: 700, color: "#b91c1c", marginBottom: 4 }}>
          {t("delivery.bulkDangerTitle", { defaultValue: "Taking links away" })}
        </div>
        <p style={{ fontSize: 13, color: "#7f1d1d", marginBottom: 16 }}>
          {t("delivery.bulkDangerHint", {
            defaultValue: "Both of these affect customers who already have a link in their hands. Neither can be undone.",
          })}
        </p>

        <div className="asf-grid" style={{ alignItems: "center" }}>
          <div style={{ flex: "1 1 320px" }}>
            <div className="asf-lbl">{t("delivery.bulkStopAll", { defaultValue: "Stop every link" })}</div>
            <span style={{ fontSize: 12, color: "#64748b", marginTop: 4, display: "block" }}>
              {t("delivery.bulkStopAllHint", {
                defaultValue:
                  "Blocks every link this organization has ever sent out, for every customer, at once. Use this " +
                  "only if links have gone somewhere they should not have. There is no undo — you would have to " +
                  "create and send a new run.",
              })}
            </span>
          </div>
          <button className="asf-btn" style={{ color: "#b91c1c", borderColor: "#fecaca" }} disabled={busy} onClick={onRevokeAll}>
            {t("delivery.bulkStopAllBtn", { defaultValue: "Stop every link" })}
          </button>
        </div>

        <div style={{ borderTop: "1px solid #fecaca", margin: "18px 0" }} />

        <div className="asf-grid" style={{ alignItems: "center" }}>
          <div style={{ flex: "1 1 320px" }}>
            <div className="asf-lbl">{t("delivery.bulkTurnOffTitle", { defaultValue: "Turn bulk links off" })}</div>
            <span style={{ fontSize: 12, color: "#64748b", marginTop: 4, display: "block" }}>
              {t("delivery.bulkTurnOffHint", {
                defaultValue: "Stops new links being created. Links already sent out keep working — use “Stop every link” for that.",
              })}
            </span>
          </div>
          <button className="asf-btn" disabled={busy} onClick={() => onSavePolicy({ batchLinksEnabled: false })}>
            {t("delivery.bulkTurnOff", { defaultValue: "Turn off" })}
          </button>
        </div>
      </div>
    </>
  );
}

function Step({ n, title, optional }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
                     width: 24, height: 24, borderRadius: 999, background: "#ede9fe",
                     color: "#6d28d9", fontWeight: 700, fontSize: 12.5 }}>{n}</span>
      <span style={{ fontWeight: 700, color: "#0f172a" }}>{title}</span>
      {/* Said on the step itself, not buried in a hint: an operator must be able to see at a glance that
          they are allowed to skip this one. */}
      {optional && (
        <span style={{ fontSize: 11.5, color: "#64748b", background: "#f1f5f9", borderRadius: 999,
                       padding: "2px 8px", fontWeight: 600 }}>{optional}</span>
      )}
    </div>
  );
}

function csvField(v) {
  const s = v == null ? "" : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * SETTINGS.
 *
 * This tab is read by a business operator, not by the engineer who built the portal, so every control is
 * written as the question it answers ("How long does a new link last?") with one plain sentence underneath
 * saying what happens if it changes. The old shape — eight bare number boxes in a row labelled "Bounds" —
 * was accurate and unreadable: nothing on screen said what a "page size ceiling" did to a customer.
 *
 * The three policy cards share ONE save, at the bottom, because they are one row in one table and a save
 * per card would suggest otherwise. The portal address keeps its own button: it is the single field the
 * server can refuse outright, and a refusal must leave what was typed on screen next to the reason.
 *
 * Trusted issuers stay, but folded away behind "Advanced" — it is the one thing here that a business user
 * cannot fill in alone, and leaving a PEM box open on the page made the whole screen look like it belonged
 * to somebody else.
 */
function SettingsTab({ t, busy, policy, issuers, algorithms, onSave, onAddIssuer, onRemoveIssuer }) {
  const [draft, setDraft] = useState({});
  const [advanced, setAdvanced] = useState(false);
  const [issuerForm, setIssuerForm] = useState({ name: "", issuer: "", audience: "", algorithm: algorithms[0] || "RS256", publicKeyPem: "", subjectClaim: "sub" });

  const value = (key, fallback) => (draft[key] !== undefined ? draft[key] : (policy?.[key] ?? fallback));
  const set = (key, v) => setDraft({ ...draft, [key]: v });
  const dirty = Object.keys(draft).length > 0;

  // The portal address saves on its own button rather than through the policy draft: it is the one field
  // here the server can refuse outright (it must be an https origin), and a refusal has to leave what the
  // operator typed on screen beside the reason instead of snapping back to the stored value.
  const [urlDraft, setUrlDraft] = useState(null);
  const urlValue = urlDraft !== null ? urlDraft : (policy?.portalBaseUrl ?? "");
  const urlDirty = urlValue.trim() !== (policy?.portalBaseUrl ?? "");
  const saveUrl = async () => {
    const ok = await onSave({ portalBaseUrl: urlValue.trim() });
    if (ok) setUrlDraft(null);
  };

  // Formats are stored as a CSV string but chosen as a set: an operator picking file types should not have
  // to know that the comma matters. Anything already stored that is not one of the two the portal offers is
  // kept as a chip of its own rather than silently dropped on the next save.
  const formatSet = String(value("allowedFormats", "PDF") || "")
    .split(/[,;\s]+/).map((f) => f.trim().toUpperCase()).filter(Boolean);
  const formatChoices = ["PDF", "TIFF", ...formatSet.filter((f) => f !== "PDF" && f !== "TIFF")];
  const toggleFormat = (f) => {
    const has = formatSet.includes(f);
    // The server refuses an empty format list — the portal could then serve nothing at all — so the last
    // one standing does not come off.
    if (has && formatSet.length === 1) return;
    const next = has ? formatSet.filter((x) => x !== f) : [...formatSet, f];
    set("allowedFormats", next.join(","));
  };

  const save = async () => {
    await onSave({
      defaultValiditySeconds: Number(value("defaultValiditySeconds", 7 * DAY)),
      maxValiditySeconds: Number(value("maxValiditySeconds", 30 * DAY)),
      allowedFormats: value("allowedFormats", "PDF"),
      requireAccessCode: !!value("requireAccessCode", false),
      sessionTtlSeconds: Number(value("sessionTtlSeconds", 900)),
      maxPageSize: Number(value("maxPageSize", 50)),
      lockoutThreshold: Number(value("lockoutThreshold", 5)),
      lockoutSeconds: Number(value("lockoutSeconds", 900)),
      maxDocumentSetSize: Number(value("maxDocumentSetSize", 500)),
      portalTitle: value("portalTitle", "") || null,
    });
    setDraft({});
  };

  const submitIssuer = async (e) => {
    e.preventDefault();
    await onAddIssuer(issuerForm);
    setIssuerForm({ name: "", issuer: "", audience: "", algorithm: algorithms[0] || "RS256", publicKeyPem: "", subjectClaim: "sub" });
    setAdvanced(true);
  };

  const days = t("delivery.unitDays", { defaultValue: "days" });
  const minutes = t("delivery.unitMinutes", { defaultValue: "minutes" });

  return (
    <>
      {/* Where the portal actually IS — a per-tenant setting, because tenants do not share a DMZ host and
          an operator who can open the portal has to be able to say where it opens. Left empty it falls back
          to the address configured on the API, which stays right for a single-tenant install. The resolved
          link is echoed underneath: what is stored and what a customer will actually receive are different
          questions, and the second one is the one that matters. */}
      <div className="asf-card">
        <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>
          {t("delivery.portalAddress", { defaultValue: "Portal address" })}
        </h3>
        <p style={{ fontSize: 12.5, color: "#64748b", margin: "0 0 14px" }}>
          {t("delivery.portalAddressSub", {
            defaultValue: "The web page your customers open. Every link you send them points here.",
          })}
        </p>
        <div className="asf-grid">
          <div className="asf-field" style={{ flex: "1 1 340px" }}>
            <input className="asf-input" type="url" value={urlValue} placeholder="https://portal.bank.example"
              onChange={(e) => setUrlDraft(e.target.value)} />
          </div>
          <button className="asf-cta" onClick={saveUrl} disabled={busy || !urlDirty}>
            {t("common.save", { defaultValue: "Save" })}
          </button>
        </div>

        {policy?.portalBaseUrlEffective ? (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 11.5, color: "#64748b", marginBottom: 5 }}>
              {t("delivery.portalAddressOpens", { defaultValue: "Customers land on:" })}
            </div>
            {/* Rendered as a link only when it really is http(s). The server refuses anything else, but an
                href is not the place to find out that an older row slipped through. */}
            {/^https?:\/\//i.test(policy.portalBaseUrlEffective) ? (
              <a href={`${policy.portalBaseUrlEffective}/portal`} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 14, fontWeight: 600, color: "#6d28d9", wordBreak: "break-all" }}>
                {policy.portalBaseUrlEffective}/portal
              </a>
            ) : (
              <code style={{ fontSize: 13.5, color: "#334155", wordBreak: "break-all" }}>
                {policy.portalBaseUrlEffective}/portal
              </code>
            )}
            {policy.portalBaseUrlInherited && (
              <p style={{ fontSize: 12, color: "#7c3aed", margin: "8px 0 0" }}>
                {t("delivery.portalAddressInherited", {
                  defaultValue: "Inherited from this server's configuration. Enter an address above to override it for this organization.",
                })}
              </p>
            )}
          </div>
        ) : (
          <div style={{ border: "1px solid #fde68a", background: "#fffbeb", borderRadius: 12,
            padding: "12px 14px", marginTop: 14 }}>
            <div style={{ fontWeight: 700, color: "#92400e", fontSize: 13 }}>
              {t("delivery.portalAddressUnset", { defaultValue: "No portal address is configured" })}
            </div>
            <div style={{ fontSize: 12.5, color: "#78350f", marginTop: 6 }}>
              {t("delivery.portalAddressUnsetHint", {
                defaultValue: "Deliveries come back as a bare token and you compose the link yourself. Enter the portal host's public address above.",
              })}
            </div>
          </div>
        )}
      </div>

      <div className="asf-card">
        <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>{t("delivery.linksCard", { defaultValue: "Links" })}</h3>
        <p style={{ fontSize: 12.5, color: "#64748b", margin: "0 0 4px" }}>
          {t("delivery.linksCardSub", { defaultValue: "How long a link you send stays usable, and what it hands over." })}
        </p>

        <SettingRow
          title={t("delivery.setDefaultValidity", { defaultValue: "How long a new link lasts" })}
          hint={t("delivery.setDefaultValidityHint", {
            defaultValue: "Filled in for whoever creates a delivery. They can choose less; this is only the starting point.",
          })}>
          <NumUnit value={Math.round(value("defaultValiditySeconds", 7 * DAY) / DAY)} unit={days}
            min={1} max={365} onChange={(v) => set("defaultValiditySeconds", v * DAY)} />
        </SettingRow>

        <SettingRow
          title={t("delivery.setMaxValidity", { defaultValue: "The longest a link may last" })}
          hint={t("delivery.setMaxValidityHint", {
            defaultValue: "A hard ceiling. Nobody in your organization can create a link that outlives this, whatever they type.",
          })}>
          <NumUnit value={Math.round(value("maxValiditySeconds", 30 * DAY) / DAY)} unit={days}
            min={1} max={365} onChange={(v) => set("maxValiditySeconds", v * DAY)} />
        </SettingRow>

        <SettingRow
          title={t("delivery.setFormats", { defaultValue: "What a recipient may download" })}
          hint={t("delivery.setFormatsHint", {
            defaultValue: "The file types the portal offers. PDF is the usual answer; at least one must stay selected.",
          })}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {formatChoices.map((f) => {
              const on = formatSet.includes(f);
              return (
                <button key={f} type="button" onClick={() => toggleFormat(f)}
                  style={{
                    appearance: "none", cursor: "pointer", padding: "8px 16px", borderRadius: 10,
                    fontSize: 13, fontWeight: 700, letterSpacing: ".02em",
                    border: `1px solid ${on ? "#7c3aed" : "#e2e8f0"}`,
                    background: on ? "#f5f3ff" : "#fff", color: on ? "#6d28d9" : "#64748b",
                  }}>
                  {on ? "✓ " : ""}{f}
                </button>
              );
            })}
          </div>
        </SettingRow>
      </div>

      <div className="asf-card">
        <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>{t("delivery.securityCard", { defaultValue: "Security" })}</h3>
        <p style={{ fontSize: 12.5, color: "#64748b", margin: "0 0 4px" }}>
          {t("delivery.securityCardSub", { defaultValue: "How a recipient proves the link is really theirs." })}
        </p>

        <SettingRow
          title={t("delivery.setRequireCode", { defaultValue: "Always ask for an access code" })}
          hint={t("delivery.setRequireCodeHint", {
            defaultValue: "On: holding the link is never enough — the recipient must also type the code you gave them. Off: whoever creates a delivery decides.",
          })}>
          <ToggleSwitch checked={!!value("requireAccessCode", false)}
            onChange={(v) => set("requireAccessCode", v)}
            label={value("requireAccessCode", false)
              ? t("common.on", { defaultValue: "On" })
              : t("common.off", { defaultValue: "Off" })} />
        </SettingRow>

        <SettingRow
          title={t("delivery.setLockoutTries", { defaultValue: "Wrong codes before we stop answering" })}
          hint={t("delivery.setLockoutTriesHint", {
            defaultValue: "Guesses in a row. One correct code clears the count, so an ordinary typo costs nothing.",
          })}>
          <NumUnit value={value("lockoutThreshold", 5)} unit={t("delivery.unitTries", { defaultValue: "tries" })}
            min={1} max={100} onChange={(v) => set("lockoutThreshold", v)} />
        </SettingRow>

        <SettingRow
          title={t("delivery.setLockoutMinutes", { defaultValue: "Then make them wait" })}
          hint={t("delivery.setLockoutMinutesHint", {
            defaultValue: "How long that recipient is locked out. You can also unlock someone by hand from the Recipients tab.",
          })}>
          <NumUnit value={Math.round(value("lockoutSeconds", 900) / 60)} unit={minutes}
            min={1} max={1440} onChange={(v) => set("lockoutSeconds", v * 60)} />
        </SettingRow>

        <SettingRow
          title={t("delivery.setSession", { defaultValue: "Sign a recipient out after" })}
          hint={t("delivery.setSessionHint", {
            defaultValue: "Counted from the moment they open the link. When it runs out they simply open the same link again.",
          })}>
          <NumUnit value={Math.round(value("sessionTtlSeconds", 900) / 60)} unit={minutes}
            min={1} max={1440} onChange={(v) => set("sessionTtlSeconds", v * 60)} />
        </SettingRow>
      </div>

      <div className="asf-card">
        <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>{t("delivery.viewCard", { defaultValue: "What recipients see" })}</h3>
        <p style={{ fontSize: 12.5, color: "#64748b", margin: "0 0 4px" }}>
          {t("delivery.viewCardSub", { defaultValue: "The page your customer lands on. Colours and logo live on the Branding tab." })}
        </p>

        <SettingRow wide
          title={t("delivery.setHeading", { defaultValue: "Heading on the portal" })}
          hint={t("delivery.setHeadingHint", {
            defaultValue: "Shown above the documents. Leave it empty and the name from the Branding tab is used instead.",
          })}>
          <input className="asf-input" value={value("portalTitle", "") || ""} maxLength={200}
            placeholder={t("delivery.setHeadingPh", { defaultValue: "Your documents" })}
            onChange={(e) => set("portalTitle", e.target.value)} />
        </SettingRow>

        <SettingRow
          title={t("delivery.setPageSize", { defaultValue: "Documents shown per page" })}
          hint={t("delivery.setPageSizeHint", {
            defaultValue: "A larger page means fewer clicks but a slower load for the customer. 50 suits most archives.",
          })}>
          <NumUnit value={value("maxPageSize", 50)} unit={t("delivery.unitDocuments", { defaultValue: "documents" })}
            min={1} max={500} onChange={(v) => set("maxPageSize", v)} />
        </SettingRow>
      </div>

      {/* One save for the three cards above — they are one policy row, and a button per card would say
          otherwise. Revert is here because an operator who has changed four numbers has no other way back. */}
      <div className="asf-card">
        <div className="asf-grid" style={{ alignItems: "center" }}>
          <button className="asf-cta" onClick={save} disabled={busy || !dirty}>
            {t("common.save", { defaultValue: "Save" })}
          </button>
          <button className="asf-btn" onClick={() => setDraft({})} disabled={busy || !dirty}>
            {t("common.revert", { defaultValue: "Revert" })}
          </button>
          <span style={{ fontSize: 12.5, color: dirty ? "#92400e" : "#94a3b8" }}>
            {dirty
              ? t("delivery.unsavedChanges", { defaultValue: "Not saved yet — nothing changes for recipients until you save." })
              : t("delivery.allSaved", { defaultValue: "Everything on this page is saved." })}
          </span>
        </div>
      </div>

      {/* Trusted issuers. Folded away by default: it is the one thing on this screen a business operator
          cannot fill in without their development team, and an open PEM box made the page look like it was
          meant for somebody else. Issuers that already exist are listed even while it is folded — a trust
          relationship that is live should never be invisible. */}
      <div className="asf-card">
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 320px", minWidth: 0 }}>
            <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>
              {t("delivery.issuersCard", { defaultValue: "Sign-in from your own website" })}
              <span className="asf-chip" style={{ background: "#f1f5f9", color: "#64748b", marginLeft: 8, verticalAlign: "middle" }}>
                {t("common.advanced", { defaultValue: "Advanced" })}
              </span>
            </h3>
            <p style={{ fontSize: 12.5, color: "#64748b", margin: 0 }}>
              {t("delivery.issuersPlain", {
                defaultValue: "Most organizations never need this. Set it up only if your customers already sign in on your own website and should reach their documents from there without a second code — your development team provides the values.",
              })}
            </p>
          </div>
          <button className="asf-btn" onClick={() => setAdvanced(!advanced)}>
            {advanced
              ? t("common.hide", { defaultValue: "Hide" })
              : t("delivery.issuersSetUp", { defaultValue: "Set this up" })}
          </button>
        </div>

        {issuers.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 18 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #eef0f5" }}>
                <Th>{t("delivery.name", { defaultValue: "Name" })}</Th>
                <Th>{t("delivery.issClaim", { defaultValue: "Expected iss" })}</Th>
                <Th>{t("delivery.algorithm", { defaultValue: "Algorithm" })}</Th>
                <Th>{t("common.status", { defaultValue: "Status" })}</Th>
                <Th align="right">{t("common.actions", { defaultValue: "Actions" })}</Th>
              </tr>
            </thead>
            <tbody>
              {issuers.map((i) => (
                <tr key={i.id} className="asf-row" style={{ borderBottom: "1px solid #f4f5f8" }}>
                  <Td><b>{i.name}</b></Td>
                  <Td style={{ fontSize: 12 }}>{i.issuer}</Td>
                  <Td>{i.algorithm}</Td>
                  <Td>
                    <span className={`asf-status asf-status--${i.isActive ? "on" : "off"}`}>
                      <i className="asf-status__dot" />
                      {i.isActive ? t("delivery.active", { defaultValue: "Active" }) : t("delivery.inactive", { defaultValue: "Inactive" })}
                    </span>
                  </Td>
                  <Td align="right">
                    {i.isActive && (
                      <button className="asf-btn" style={{ padding: "6px 12px", fontSize: 12.5 }}
                        disabled={busy} onClick={() => onRemoveIssuer(i)}>
                        {t("delivery.deactivate", { defaultValue: "Deactivate" })}
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {advanced && (
          <form onSubmit={submitIssuer} style={{ marginTop: 18, borderTop: "1px solid #f1f5f9", paddingTop: 18 }}>
            <p style={{ fontSize: 12.5, color: "#64748b", margin: "0 0 14px" }}>
              {t("delivery.issuersHint", {
                defaultValue: "Let the customer's own application sign its users in. Paste its PUBLIC key — this server only ever verifies, never signs.",
              })}
            </p>
            <div className="asf-grid">
              <IssuerField label={t("delivery.name", { defaultValue: "Name" })}
                hint={t("delivery.issuerNameHint", { defaultValue: "A label for your own list, such as “Retail internet banking”." })}
                flex="1 1 220px">
                <input className="asf-input" value={issuerForm.name} required
                  onChange={(e) => setIssuerForm({ ...issuerForm, name: e.target.value })} />
              </IssuerField>
              <IssuerField label={t("delivery.issClaim", { defaultValue: "Expected iss" })}
                hint={t("delivery.issClaimHint", { defaultValue: "The issuer your application writes into the tokens it signs." })}
                flex="1 1 240px">
                <input className="asf-input" value={issuerForm.issuer} required placeholder="https://selfcare.example.com"
                  onChange={(e) => setIssuerForm({ ...issuerForm, issuer: e.target.value })} />
              </IssuerField>
              <IssuerField label={t("delivery.audClaim", { defaultValue: "Expected aud" })}
                hint={t("delivery.audClaimHint", { defaultValue: "The audience those tokens carry. Anything else is refused." })}
                flex="1 1 220px">
                <input className="asf-input" value={issuerForm.audience} required
                  onChange={(e) => setIssuerForm({ ...issuerForm, audience: e.target.value })} />
              </IssuerField>
              <IssuerField label={t("delivery.algorithm", { defaultValue: "Algorithm" })}
                hint={t("delivery.algorithmHint", { defaultValue: "The signature algorithm your application uses." })}
                flex="0 1 150px">
                <Dropdown value={issuerForm.algorithm}
                  onChange={(v) => setIssuerForm({ ...issuerForm, algorithm: v })}
                  options={algorithms.map((a) => ({ value: a, label: a }))} />
              </IssuerField>
              <IssuerField label={t("delivery.subjectClaim", { defaultValue: "Subject claim" })}
                hint={t("delivery.subjectClaimHint", { defaultValue: "Which field in the token names the customer. Usually sub." })}
                flex="0 1 150px">
                <input className="asf-input" value={issuerForm.subjectClaim}
                  onChange={(e) => setIssuerForm({ ...issuerForm, subjectClaim: e.target.value })} />
              </IssuerField>
            </div>
            <IssuerField label={t("delivery.publicKey", { defaultValue: "Public key (PEM)" })}
              hint={t("delivery.publicKeyHint", { defaultValue: "The PUBLIC half only — never the private key. It is used to check signatures, nothing else." })}
              style={{ marginTop: 12 }}>
              <textarea className="asf-input" rows={4} value={issuerForm.publicKeyPem} required
                placeholder="-----BEGIN PUBLIC KEY-----"
                onChange={(e) => setIssuerForm({ ...issuerForm, publicKeyPem: e.target.value })} />
            </IssuerField>
            <button className="asf-cta" type="submit" style={{ marginTop: 12 }} disabled={busy}>
              {t("delivery.addIssuer", { defaultValue: "Add issuer" })}
            </button>
          </form>
        )}
      </div>
    </>
  );
}

/** One setting: what it is and what it does on the left, the control on the right. */
function SettingRow({ title, hint, children, wide }) {
  return (
    <div style={{
      display: "flex", gap: 18, alignItems: "flex-start", flexWrap: "wrap",
      padding: "16px 0", borderTop: "1px solid #f1f5f9",
    }}>
      <div style={{ flex: "1 1 320px", minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: "#1e293b" }}>{title}</div>
        <p style={{ fontSize: 12, color: "#64748b", margin: "4px 0 0", lineHeight: 1.55 }}>{hint}</p>
      </div>
      <div style={{ flex: wide ? "1 1 260px" : "0 0 auto", minWidth: 0, paddingTop: 2 }}>{children}</div>
    </div>
  );
}

/** A number with its unit spelled out beside it, so "15" is never left to mean whatever the reader assumes. */
function NumUnit({ value, unit, min = 1, max, onChange }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 9 }}>
      <input className="asf-input" type="number" min={min} max={max} value={value}
        style={{ width: 96, textAlign: "right" }}
        onChange={(e) => onChange(Number(e.target.value) || 0)} />
      <span style={{ fontSize: 13, color: "#475569" }}>{unit}</span>
    </div>
  );
}

/** Labelled field with a sentence under it — the issuer form needs one on every box. */
function IssuerField({ label, hint, children, flex, style }) {
  return (
    <div className="asf-field" style={{ flex, ...style }}>
      <label className="asf-lbl">{label}</label>
      {children}
      {hint && <p style={{ fontSize: 11.5, color: "#64748b", margin: "6px 0 0", lineHeight: 1.5 }}>{hint}</p>}
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th style={{ textAlign: align, padding: "10px 14px", color: "#64748b", fontWeight: 600, fontSize: 11.5, textTransform: "uppercase", letterSpacing: ".05em" }}>{children}</th>;
}

function Td({ children, align = "left", style }) {
  return <td style={{ textAlign: align, padding: "11px 14px", color: "#334155", ...style }}>{children}</td>;
}
